/**
 * Wekalp consent widget (CM-3, UI side).
 *
 * One script tag and a div. Renders the notice summary and one toggle per
 * purpose, collects an explicit yes or no for each, and posts to the capture
 * API. Deliberate constraints, all of them legal rather than cosmetic:
 *
 *   - Nothing is pre-ticked. S.6(1) requires a clear affirmative action, so
 *     every switch starts off and the principal has to move it.
 *   - Purposes are never bundled. One control each, one record each.
 *   - Optional purposes say so, and refusing one is never presented as a
 *     problem — the host page is told, and decides nothing on our behalf.
 *   - There is no "accept all" shortcut without an equally weighted "reject
 *     all"; both are plain buttons of the same visual weight.
 *   - Rendered in a shadow root so the host page's CSS cannot restyle a
 *     rejection button into invisibility.
 *
 * Usage:
 *   <div id="consent"></div>
 *   <script src="/widget.js"
 *           data-api="https://consent.kaveri.example"
 *           data-key="wkcp_xxxx.yyyy"
 *           data-target="#consent"
 *           data-lang="en"></script>
 *
 * The host page supplies the identifier when it has one:
 *   WekalpConsent.submit({ identifier: 'asha@example.com', identifierType: 'email' })
 * or lets the widget read it from a field:
 *   data-identifier-field="#email" data-identifier-type="email"
 */

import {
  SHARED_CSS, svg, loadA11y, applyA11y as applyDisplay, displayStrip as sharedDisplayStrip,
  langStrip as sharedLangStrip, railButton, alwaysOn, poweredBy, dt,
} from './lib/display.js';

(function () {
  'use strict';

  var STRINGS = {
    en: {
      chooseTitle: 'Choose what you allow',
      readNotice: 'Read the full notice',
      required: 'Required for service',
      optional: 'Optional: saying no changes nothing about your service',
      confirm: 'Confirm my choices',
      acceptAll: 'Allow all',
      rejectAll: 'Allow none',
      saved: 'Your choices are saved.',
      changeLater: 'Change or withdraw these at any time',
      error: 'We could not save your choices. Please try again.',
      dataUsed: 'Data used',
      sensitivity: 'Sensitivity', sensLow: 'Low', sensMedium: 'Medium', sensHigh: 'High',
      keptFor: 'Kept for',
      untilWithdrawn: 'until you withdraw',
      sharedWith: 'Shared with',
      notShared: 'Not shared',
      days: 'days',
      close: 'Close',
      language: 'Language',
      yourRights: 'Your rights',
      grievance: 'Grievance officer',
      board: 'Complaining to the Data Protection Board',
      statedTitle: 'Processed without asking for consent',
      statedTag: 'Permitted by law: no choice is asked',
      ground: 'Legal ground',
      codeSent: 'We sent a code to {to}. Enter it to confirm your choices.',
      codePlaceholder: '6-digit code',
      confirmCode: 'Confirm',
      codeExpires: 'The code works for {n} minutes.',
      startOver: 'Start over',
      ageQuestion: 'Who is this consent for?',
      ageAdult: 'Myself, 18 or over',
      ageChild: 'A child under 18, as their parent or guardian',
      ageDisability: 'A person with disability, as their lawful guardian',
      ageRequired: 'Say who this consent is for.',
      guardianContact: 'Parent or guardian email or mobile number',
      relationship: 'Relationship',
      relParent: 'Parent',
      relGuardian: 'Lawful guardian',
      guardianRequired: 'Enter the parent or guardian email or mobile number.',
      notForChildren: 'Not offered to children',
      guardianCodeSent: 'We sent a code to {to}. The parent or guardian enters it here.',
      declAdult: 'I am 18 or over',
      declGuardian: 'I am the parent or lawful guardian of this person',
      declRequired: 'Both statements are needed.',
      guardianDone: 'Thank you. The choices are recorded once the parent or guardian has been checked.',
      resend: 'Send a new code',
      contactBy: 'Contact me by',
      ch_email: 'Email', ch_sms: 'SMS', ch_whatsapp: 'WhatsApp', ch_phone: 'Phone call', ch_post: 'Post', ch_push: 'App notification',
      requiredShort: 'Required',
      optionalShort: 'Optional',
      details: 'What we use',
      hideDetails: 'Hide',
      recipientOne: '1 recipient',
      recipientMany: '{n} recipients',
      year: 'year', years: 'years', month: 'month', months: 'months',
      kept: 'Kept',
      doneTitle: 'Your choice is saved',
      doneLine: 'It is recorded and signed in the consent ledger. Anyone can check it with the receipt.',
      doneRecord: 'Your record in the consent ledger',
      actGiven: 'Consent given',
      actWithdrawn: 'Consent withdrawn',
      actUpdated: 'Choices updated',
      doneEntry: 'Ledger entry',
      doneWhen: 'Date and time',
      doneHash: 'Event hash',
      doneCopy: 'Copy',
      doneCopied: 'Copied',
      doneMore: '{n} records, one per purpose',
      doneAllowed: 'Allowed',
      doneNotAllowed: 'Not allowed',
      doneStated: 'Informed',
      doneVerify: 'Verify',
      donePrint: 'Receipt (printable)',
      doneJson: 'Receipt (JSON)',
      doneNoticeHash: 'Notice text',
      doneNoticeNote: 'The notice content hash identifies the text you read, not your choice.',
      doneContinue: 'Continue',
      doneReceiptFailed: 'The receipt is not ready yet. Try again in a minute.',
    },
    hi: {
      chooseTitle: 'चुनें कि आप किसकी अनुमति देते हैं',
      readNotice: 'पूरी सूचना पढ़ें',
      required: 'सेवा के लिए आवश्यक',
      optional: 'वैकल्पिक: मना करने से आपकी सेवा पर कोई असर नहीं पड़ता',
      confirm: 'मेरी पसंद की पुष्टि करें',
      acceptAll: 'सभी की अनुमति दें',
      rejectAll: 'किसी की अनुमति न दें',
      saved: 'आपकी पसंद सहेज ली गई है।',
      doneTitle: 'आपकी पसंद सहेज ली गई है',
      doneLine: 'यह सहमति लेजर में दर्ज और हस्ताक्षरित है। रसीद से कोई भी इसकी जाँच कर सकता है।',
      doneRecord: 'सहमति लेजर में आपका रिकॉर्ड',
      actGiven: 'सहमति दी गई',
      actWithdrawn: 'सहमति वापस ली गई',
      actUpdated: 'पसंद अपडेट की गई',
      doneEntry: 'लेजर प्रविष्टि',
      doneWhen: 'तारीख और समय',
      doneHash: 'इवेंट हैश',
      doneCopy: 'कॉपी करें',
      doneCopied: 'कॉपी हो गया',
      doneMore: '{n} रिकॉर्ड, हर उद्देश्य के लिए एक',
      doneAllowed: 'अनुमति है',
      doneNotAllowed: 'अनुमति नहीं',
      doneStated: 'सूचित किया गया',
      doneVerify: 'जाँचें',
      donePrint: 'रसीद (प्रिंट योग्य)',
      doneJson: 'रसीद (JSON)',
      doneNoticeHash: 'सूचना का पाठ',
      doneNoticeNote: 'सूचना का कंटेंट हैश आपके पढ़े गए पाठ की पहचान करता है, आपकी पसंद की नहीं।',
      doneContinue: 'आगे बढ़ें',
      doneReceiptFailed: 'रसीद अभी तैयार नहीं है। एक मिनट बाद फिर प्रयास करें।',
      changeLater: 'इन्हें कभी भी बदलें या वापस लें',
      error: 'हम आपकी पसंद सहेज नहीं सके। कृपया पुनः प्रयास करें।',
      dataUsed: 'उपयोग किया गया डेटा',
      sensitivity: 'संवेदनशीलता', sensLow: 'कम', sensMedium: 'मध्यम', sensHigh: 'उच्च',
      keptFor: 'रखा जाएगा',
      untilWithdrawn: 'जब तक आप वापस न लें',
      sharedWith: 'इनके साथ साझा',
      notShared: 'साझा नहीं किया जाता',
      days: 'दिन',
      close: 'बंद करें',
      language: 'भाषा',
      yourRights: 'आपके अधिकार',
      grievance: 'शिकायत अधिकारी',
      board: 'डेटा संरक्षण बोर्ड से शिकायत',
      statedTitle: 'सहमति माँगे बिना संसाधित',
      statedTag: 'कानून द्वारा अनुमत: कोई विकल्प नहीं माँगा जाता',
      ground: 'कानूनी आधार',
      codeSent: 'हमने {to} पर एक कोड भेजा है। अपनी पसंद की पुष्टि के लिए उसे दर्ज करें।',
      codePlaceholder: '6 अंकों का कोड',
      confirmCode: 'पुष्टि करें',
      codeExpires: 'कोड {n} मिनट तक मान्य है।',
      startOver: 'फिर से शुरू करें',
      ageQuestion: 'यह सहमति किसके लिए है?',
      ageAdult: 'स्वयं के लिए, 18 वर्ष या अधिक',
      ageChild: '18 वर्ष से कम आयु के बच्चे के लिए, माता-पिता या अभिभावक के रूप में',
      ageDisability: 'दिव्यांग व्यक्ति के लिए, विधिक अभिभावक के रूप में',
      ageRequired: 'बताएं कि यह सहमति किसके लिए है।',
      guardianContact: 'माता-पिता या अभिभावक का ईमेल या मोबाइल नंबर',
      relationship: 'संबंध',
      relParent: 'माता-पिता',
      relGuardian: 'विधिक अभिभावक',
      guardianRequired: 'माता-पिता या अभिभावक का ईमेल या मोबाइल नंबर दर्ज करें।',
      notForChildren: 'बच्चों के लिए उपलब्ध नहीं',
      guardianCodeSent: 'हमने {to} पर एक कोड भेजा है। माता-पिता या अभिभावक उसे यहां दर्ज करें।',
      declAdult: 'मेरी आयु 18 वर्ष या अधिक है',
      declGuardian: 'मैं इस व्यक्ति का/की माता-पिता या विधिक अभिभावक हूं',
      declRequired: 'दोनों कथन आवश्यक हैं।',
      guardianDone: 'धन्यवाद। माता-पिता या अभिभावक की जांच के बाद पसंद दर्ज की जाएगी।',
      resend: 'नया कोड भेजें',
      contactBy: 'मुझसे संपर्क करें',
      ch_email: 'ईमेल', ch_sms: 'एसएमएस', ch_whatsapp: 'व्हाट्सऐप', ch_phone: 'फ़ोन कॉल', ch_post: 'डाक', ch_push: 'ऐप सूचना',
    },
    // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
    mr: {
      chooseTitle: 'तुम्ही कशाला परवानगी देता ते निवडा',
      readNotice: 'संपूर्ण सूचना वाचा',
      required: 'सेवेसाठी आवश्यक',
      optional: 'ऐच्छिक: नकार दिल्याने तुमच्या सेवेत कोणताही बदल होत नाही',
      confirm: 'माझ्या निवडींची पुष्टी करा',
      acceptAll: 'सर्वांना परवानगी द्या',
      rejectAll: 'कशालाही परवानगी देऊ नका',
      saved: 'तुमच्या निवडी जतन केल्या आहेत.',
      changeLater: 'या निवडी केव्हाही बदला किंवा मागे घ्या',
      error: 'आम्ही तुमच्या निवडी जतन करू शकलो नाही. कृपया पुन्हा प्रयत्न करा.',
      dataUsed: 'वापरलेला डेटा',
      sensitivity: 'संवेदनशीलता', sensLow: 'कमी', sensMedium: 'मध्यम', sensHigh: 'उच्च',
      keptFor: 'ठेवण्याचा कालावधी',
      untilWithdrawn: 'तुम्ही मागे घेईपर्यंत',
      sharedWith: 'यांच्याशी सामायिक',
      notShared: 'सामायिक केला जात नाही',
      days: 'दिवस',
      close: 'बंद करा',
      language: 'भाषा',
      yourRights: 'तुमचे अधिकार',
      grievance: 'तक्रार अधिकारी',
      board: 'डेटा संरक्षण मंडळाकडे तक्रार',
      statedTitle: 'संमती न मागता प्रक्रिया केली जाते',
      statedTag: 'कायद्याने परवानगी: कोणताही पर्याय विचारला जात नाही',
      ground: 'कायदेशीर आधार',
      codeSent: 'आम्ही {to} वर एक कोड पाठवला आहे. तुमच्या निवडींची पुष्टी करण्यासाठी तो प्रविष्ट करा.',
      codePlaceholder: '6 अंकी कोड',
      confirmCode: 'पुष्टी करा',
      codeExpires: 'हा कोड {n} मिनिटांसाठी वैध आहे.',
      startOver: 'पुन्हा सुरू करा',
      ageQuestion: 'ही संमती कोणासाठी आहे?',
      ageAdult: 'स्वतःसाठी, 18 वर्षे किंवा अधिक',
      ageChild: '18 वर्षांखालील मुलासाठी, आई-वडील किंवा पालक म्हणून',
      ageDisability: 'दिव्यांग व्यक्तीसाठी, कायदेशीर पालक म्हणून',
      ageRequired: 'ही संमती कोणासाठी आहे ते सांगा.',
      guardianContact: 'आई-वडील किंवा पालकांचा ईमेल किंवा मोबाइल नंबर',
      relationship: 'नाते',
      relParent: 'आई-वडील',
      relGuardian: 'कायदेशीर पालक',
      guardianRequired: 'आई-वडील किंवा पालकांचा ईमेल किंवा मोबाइल नंबर प्रविष्ट करा.',
      notForChildren: 'मुलांसाठी उपलब्ध नाही',
      guardianCodeSent: 'आम्ही {to} वर एक कोड पाठवला आहे. आई-वडील किंवा पालकांनी तो येथे प्रविष्ट करावा.',
      declAdult: 'माझे वय 18 वर्षे किंवा अधिक आहे',
      declGuardian: 'मी या व्यक्तीचे आई-वडील किंवा कायदेशीर पालक आहे',
      declRequired: 'दोन्ही विधाने आवश्यक आहेत.',
      guardianDone: 'धन्यवाद. आई-वडील किंवा पालकांची पडताळणी झाल्यानंतर निवडी नोंदवल्या जातील.',
      resend: 'नवीन कोड पाठवा',
      contactBy: 'माझ्याशी संपर्क साधा',
      ch_email: 'ईमेल',
      ch_sms: 'एसएमएस',
      ch_whatsapp: 'WhatsApp',
      ch_phone: 'फोन कॉल',
      ch_post: 'टपाल', ch_push: 'ॲप सूचना',
    },
    // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
    gu: {
      chooseTitle: 'તમે શેની મંજૂરી આપો છો તે પસંદ કરો',
      readNotice: 'સંપૂર્ણ સૂચના વાંચો',
      required: 'સેવા માટે જરૂરી',
      optional: 'વૈકલ્પિક: ના પાડવાથી તમારી સેવામાં કોઈ ફેરફાર થતો નથી',
      confirm: 'મારી પસંદગીની પુષ્ટિ કરો',
      acceptAll: 'બધાની મંજૂરી આપો',
      rejectAll: 'કોઈની મંજૂરી ન આપો',
      saved: 'તમારી પસંદગી સાચવવામાં આવી છે.',
      changeLater: 'આને ગમે ત્યારે બદલો અથવા પાછી ખેંચો',
      error: 'અમે તમારી પસંદગી સાચવી શક્યા નથી. કૃપા કરીને ફરી પ્રયાસ કરો.',
      dataUsed: 'વપરાતો ડેટા',
      sensitivity: 'સંવેદનશીલતા', sensLow: 'ઓછી', sensMedium: 'મધ્યમ', sensHigh: 'ઊંચી',
      keptFor: 'રાખવાનો સમયગાળો',
      untilWithdrawn: 'તમે પાછી ખેંચો ત્યાં સુધી',
      sharedWith: 'આમની સાથે શેર',
      notShared: 'શેર કરવામાં આવતો નથી',
      days: 'દિવસ',
      close: 'બંધ કરો',
      language: 'ભાષા',
      yourRights: 'તમારા અધિકારો',
      grievance: 'ફરિયાદ અધિકારી',
      board: 'ડેટા સંરક્ષણ બોર્ડને ફરિયાદ',
      statedTitle: 'સંમતિ માંગ્યા વિના પ્રક્રિયા કરવામાં આવે છે',
      statedTag: 'કાયદા દ્વારા માન્ય: કોઈ વિકલ્પ પૂછવામાં આવતો નથી',
      ground: 'કાનૂની આધાર',
      codeSent: 'અમે {to} પર એક કોડ મોકલ્યો છે. તમારી પસંદગીની પુષ્ટિ કરવા તે દાખલ કરો.',
      codePlaceholder: '6 અંકનો કોડ',
      confirmCode: 'પુષ્ટિ કરો',
      codeExpires: 'કોડ {n} મિનિટ સુધી માન્ય છે.',
      startOver: 'ફરીથી શરૂ કરો',
      ageQuestion: 'આ સંમતિ કોના માટે છે?',
      ageAdult: 'મારા પોતાના માટે, 18 વર્ષ કે તેથી વધુ',
      ageChild: '18 વર્ષથી ઓછી ઉંમરના બાળક માટે, માતા-પિતા અથવા વાલી તરીકે',
      ageDisability: 'દિવ્યાંગ વ્યક્તિ માટે, કાનૂની વાલી તરીકે',
      ageRequired: 'આ સંમતિ કોના માટે છે તે જણાવો.',
      guardianContact: 'માતા-પિતા અથવા વાલીનો ઇમેઇલ અથવા મોબાઇલ નંબર',
      relationship: 'સંબંધ',
      relParent: 'માતા-પિતા',
      relGuardian: 'કાનૂની વાલી',
      guardianRequired: 'માતા-પિતા અથવા વાલીનો ઇમેઇલ અથવા મોબાઇલ નંબર દાખલ કરો.',
      notForChildren: 'બાળકો માટે ઉપલબ્ધ નથી',
      guardianCodeSent: 'અમે {to} પર એક કોડ મોકલ્યો છે. માતા-પિતા અથવા વાલી તેને અહીં દાખલ કરે.',
      declAdult: 'મારી ઉંમર 18 વર્ષ કે તેથી વધુ છે',
      declGuardian: 'હું આ વ્યક્તિના માતા-પિતા અથવા કાનૂની વાલી છું',
      declRequired: 'બંને નિવેદનો જરૂરી છે.',
      guardianDone: 'આભાર. માતા-પિતા અથવા વાલીની ચકાસણી પછી પસંદગી નોંધવામાં આવશે.',
      resend: 'નવો કોડ મોકલો',
      contactBy: 'મારો સંપર્ક કરો',
      ch_email: 'ઇમેઇલ',
      ch_sms: 'એસએમએસ',
      ch_whatsapp: 'WhatsApp',
      ch_phone: 'ફોન કૉલ',
      ch_post: 'ટપાલ', ch_push: 'ઍપ સૂચના',
    },
    // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
    ta: {
      chooseTitle: 'நீங்கள் எதற்கு அனுமதி அளிக்கிறீர்கள் என்பதைத் தேர்ந்தெடுக்கவும்',
      readNotice: 'முழு அறிவிப்பையும் படிக்கவும்',
      required: 'சேவைக்குத் தேவையானது',
      optional: 'விருப்பத்திற்குரியது: மறுப்பதால் உங்கள் சேவையில் எந்த மாற்றமும் இல்லை',
      confirm: 'என் தேர்வுகளை உறுதிப்படுத்தவும்',
      acceptAll: 'அனைத்தையும் அனுமதிக்கவும்',
      rejectAll: 'எதையும் அனுமதிக்க வேண்டாம்',
      saved: 'உங்கள் தேர்வுகள் சேமிக்கப்பட்டன.',
      changeLater: 'இவற்றை எப்போது வேண்டுமானாலும் மாற்றலாம் அல்லது திரும்பப் பெறலாம்',
      error: 'உங்கள் தேர்வுகளைச் சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
      dataUsed: 'பயன்படுத்தப்படும் தரவு',
      keptFor: 'வைத்திருக்கும் காலம்',
      untilWithdrawn: 'நீங்கள் திரும்பப் பெறும் வரை',
      sharedWith: 'இவர்களுடன் பகிரப்படும்',
      notShared: 'பகிரப்படுவதில்லை',
      days: 'நாட்கள்',
      close: 'மூடு',
      language: 'மொழி',
      yourRights: 'உங்கள் உரிமைகள்',
      grievance: 'குறைதீர்ப்பு அலுவலர்',
      board: 'தரவுப் பாதுகாப்பு வாரியத்திடம் புகார்',
      statedTitle: 'ஒப்புதல் கேட்காமல் செயலாக்கப்படுகிறது',
      statedTag: 'சட்டத்தால் அனுமதிக்கப்பட்டது: தேர்வு கேட்கப்படுவதில்லை',
      ground: 'சட்ட அடிப்படை',
      codeSent: '{to} க்கு ஒரு குறியீட்டை அனுப்பியுள்ளோம். உங்கள் தேர்வுகளை உறுதிப்படுத்த அதை உள்ளிடவும்.',
      codePlaceholder: '6 இலக்கக் குறியீடு',
      confirmCode: 'உறுதிப்படுத்தவும்',
      codeExpires: 'இந்தக் குறியீடு {n} நிமிடங்களுக்குச் செல்லுபடியாகும்.',
      startOver: 'மீண்டும் தொடங்கவும்',
      ageQuestion: 'இந்த ஒப்புதல் யாருக்காக?',
      ageAdult: 'எனக்காக, 18 வயது அல்லது அதற்கு மேல்',
      ageChild: '18 வயதுக்குட்பட்ட குழந்தைக்காக, பெற்றோர் அல்லது பாதுகாவலராக',
      ageDisability: 'மாற்றுத்திறனாளி ஒருவருக்காக, சட்டப்பூர்வ பாதுகாவலராக',
      ageRequired: 'இந்த ஒப்புதல் யாருக்காக என்பதைக் குறிப்பிடவும்.',
      guardianContact: 'பெற்றோர் அல்லது பாதுகாவலரின் மின்னஞ்சல் அல்லது கைபேசி எண்',
      relationship: 'உறவுமுறை',
      relParent: 'பெற்றோர்',
      relGuardian: 'சட்டப்பூர்வ பாதுகாவலர்',
      guardianRequired: 'பெற்றோர் அல்லது பாதுகாவலரின் மின்னஞ்சல் அல்லது கைபேசி எண்ணை உள்ளிடவும்.',
      notForChildren: 'குழந்தைகளுக்கு வழங்கப்படுவதில்லை',
      guardianCodeSent: '{to} க்கு ஒரு குறியீட்டை அனுப்பியுள்ளோம். பெற்றோர் அல்லது பாதுகாவலர் அதை இங்கே உள்ளிட வேண்டும்.',
      declAdult: 'எனக்கு 18 வயது அல்லது அதற்கு மேல் ஆகிறது',
      declGuardian: 'நான் இவரது பெற்றோர் அல்லது சட்டப்பூர்வ பாதுகாவலர்',
      declRequired: 'இரண்டு உறுதிமொழிகளும் தேவை.',
      guardianDone: 'நன்றி. பெற்றோர் அல்லது பாதுகாவலர் சரிபார்க்கப்பட்ட பிறகு தேர்வுகள் பதிவு செய்யப்படும்.',
      resend: 'புதிய குறியீட்டை அனுப்பவும்',
      contactBy: 'என்னைத் தொடர்புகொள்ள',
      ch_email: 'மின்னஞ்சல்',
      ch_sms: 'குறுஞ்செய்தி',
      ch_whatsapp: 'WhatsApp',
      ch_phone: 'தொலைபேசி அழைப்பு',
      ch_post: 'அஞ்சல்', ch_push: 'ஆப் அறிவிப்பு',
    },
    // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
    te: {
      chooseTitle: 'మీరు దేనికి అనుమతి ఇస్తారో ఎంచుకోండి',
      readNotice: 'పూర్తి నోటీసును చదవండి',
      required: 'సేవకు అవసరం',
      optional: 'ఐచ్ఛికం: వద్దనడం వల్ల మీ సేవలో ఎలాంటి మార్పు ఉండదు',
      confirm: 'నా ఎంపికలను నిర్ధారించండి',
      acceptAll: 'అన్నింటినీ అనుమతించండి',
      rejectAll: 'దేనినీ అనుమతించవద్దు',
      saved: 'మీ ఎంపికలు భద్రపరచబడ్డాయి.',
      changeLater: 'వీటిని ఎప్పుడైనా మార్చవచ్చు లేదా ఉపసంహరించుకోవచ్చు',
      error: 'మీ ఎంపికలను భద్రపరచలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.',
      dataUsed: 'ఉపయోగించే డేటా',
      keptFor: 'ఉంచే వ్యవధి',
      untilWithdrawn: 'మీరు ఉపసంహరించుకునే వరకు',
      sharedWith: 'వీరితో పంచుకోబడుతుంది',
      notShared: 'ఎవరితోనూ పంచుకోబడదు',
      days: 'రోజులు',
      close: 'మూసివేయండి',
      language: 'భాష',
      yourRights: 'మీ హక్కులు',
      grievance: 'ఫిర్యాదుల అధికారి',
      board: 'డేటా పరిరక్షణ బోర్డుకు ఫిర్యాదు',
      statedTitle: 'సమ్మతి అడగకుండా ప్రాసెస్ చేయబడుతుంది',
      statedTag: 'చట్టం అనుమతిస్తుంది: ఎంపిక అడగబడదు',
      ground: 'చట్టపరమైన ఆధారం',
      codeSent: 'మేము {to}కు ఒక కోడ్ పంపాము. మీ ఎంపికలను నిర్ధారించడానికి దాన్ని నమోదు చేయండి.',
      codePlaceholder: '6 అంకెల కోడ్',
      confirmCode: 'నిర్ధారించండి',
      codeExpires: 'కోడ్ {n} నిమిషాల పాటు చెల్లుబాటులో ఉంటుంది.',
      startOver: 'మళ్లీ ప్రారంభించండి',
      ageQuestion: 'ఈ సమ్మతి ఎవరి కోసం?',
      ageAdult: 'నా కోసం, 18 ఏళ్లు లేదా అంతకంటే ఎక్కువ',
      ageChild: '18 ఏళ్ల లోపు పిల్లల కోసం, తల్లిదండ్రులు లేదా సంరక్షకులుగా',
      ageDisability: 'దివ్యాంగ వ్యక్తి కోసం, చట్టబద్ధ సంరక్షకులుగా',
      ageRequired: 'ఈ సమ్మతి ఎవరి కోసమో తెలియజేయండి.',
      guardianContact: 'తల్లిదండ్రులు లేదా సంరక్షకుల ఇమెయిల్ లేదా మొబైల్ నంబర్',
      relationship: 'సంబంధం',
      relParent: 'తల్లిదండ్రులు',
      relGuardian: 'చట్టబద్ధ సంరక్షకులు',
      guardianRequired: 'తల్లిదండ్రులు లేదా సంరక్షకుల ఇమెయిల్ లేదా మొబైల్ నంబర్‌ను నమోదు చేయండి.',
      notForChildren: 'పిల్లలకు అందుబాటులో లేదు',
      guardianCodeSent: 'మేము {to}కు ఒక కోడ్ పంపాము. తల్లిదండ్రులు లేదా సంరక్షకులు దాన్ని ఇక్కడ నమోదు చేయాలి.',
      declAdult: 'నా వయస్సు 18 ఏళ్లు లేదా అంతకంటే ఎక్కువ',
      declGuardian: 'నేను ఈ వ్యక్తి యొక్క తల్లిదండ్రులలో ఒకరిని లేదా చట్టబద్ధ సంరక్షకుడిని',
      declRequired: 'రెండు ప్రకటనలూ అవసరం.',
      guardianDone: 'ధన్యవాదాలు. తల్లిదండ్రులు లేదా సంరక్షకులను తనిఖీ చేసిన తర్వాత ఎంపికలు నమోదు చేయబడతాయి.',
      resend: 'కొత్త కోడ్ పంపండి',
      contactBy: 'నన్ను సంప్రదించే విధానం',
      ch_email: 'ఇమెయిల్',
      ch_sms: 'ఎస్ఎంఎస్',
      ch_whatsapp: 'WhatsApp',
      ch_phone: 'ఫోన్ కాల్',
      ch_post: 'తపాలా', ch_push: 'యాప్ నోటిఫికేషన్',
    },
    // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
    bn: {
      chooseTitle: 'আপনি কীসের অনুমতি দিচ্ছেন তা বেছে নিন',
      readNotice: 'সম্পূর্ণ বিজ্ঞপ্তি পড়ুন',
      required: 'পরিষেবার জন্য আবশ্যক',
      optional: 'ঐচ্ছিক: না বললে আপনার পরিষেবায় কোনো পরিবর্তন হয় না',
      confirm: 'আমার পছন্দ নিশ্চিত করুন',
      acceptAll: 'সবকিছুর অনুমতি দিন',
      rejectAll: 'কোনোটির অনুমতি দেবেন না',
      saved: 'আপনার পছন্দ সংরক্ষিত হয়েছে।',
      changeLater: 'যেকোনো সময় এগুলি পরিবর্তন বা প্রত্যাহার করুন',
      error: 'আমরা আপনার পছন্দ সংরক্ষণ করতে পারিনি। অনুগ্রহ করে আবার চেষ্টা করুন।',
      dataUsed: 'ব্যবহৃত ডেটা',
      keptFor: 'রাখার মেয়াদ',
      untilWithdrawn: 'আপনি প্রত্যাহার না করা পর্যন্ত',
      sharedWith: 'যাদের সঙ্গে শেয়ার করা হয়',
      notShared: 'শেয়ার করা হয় না',
      days: 'দিন',
      close: 'বন্ধ করুন',
      language: 'ভাষা',
      yourRights: 'আপনার অধিকার',
      grievance: 'অভিযোগ আধিকারিক',
      board: 'ডেটা সুরক্ষা বোর্ডে অভিযোগ',
      statedTitle: 'সম্মতি না চেয়ে প্রক্রিয়াকরণ করা হয়',
      statedTag: 'আইন দ্বারা অনুমোদিত: কোনো বিকল্প চাওয়া হয় না',
      ground: 'আইনি ভিত্তি',
      codeSent: 'আমরা {to}-এ একটি কোড পাঠিয়েছি। আপনার পছন্দ নিশ্চিত করতে সেটি লিখুন।',
      codePlaceholder: '6 অঙ্কের কোড',
      confirmCode: 'নিশ্চিত করুন',
      codeExpires: 'কোডটি {n} মিনিট পর্যন্ত বৈধ।',
      startOver: 'আবার শুরু করুন',
      ageQuestion: 'এই সম্মতি কার জন্য?',
      ageAdult: 'নিজের জন্য, 18 বছর বা তার বেশি',
      ageChild: '18 বছরের কম বয়সী শিশুর জন্য, পিতামাতা বা অভিভাবক হিসেবে',
      ageDisability: 'প্রতিবন্ধী ব্যক্তির জন্য, আইনসম্মত অভিভাবক হিসেবে',
      ageRequired: 'এই সম্মতি কার জন্য তা জানান।',
      guardianContact: 'পিতামাতা বা অভিভাবকের ইমেল বা মোবাইল নম্বর',
      relationship: 'সম্পর্ক',
      relParent: 'পিতামাতা',
      relGuardian: 'আইনসম্মত অভিভাবক',
      guardianRequired: 'পিতামাতা বা অভিভাবকের ইমেল বা মোবাইল নম্বর লিখুন।',
      notForChildren: 'শিশুদের জন্য উপলব্ধ নয়',
      guardianCodeSent: 'আমরা {to}-এ একটি কোড পাঠিয়েছি। পিতামাতা বা অভিভাবক সেটি এখানে লিখবেন।',
      declAdult: 'আমার বয়স 18 বছর বা তার বেশি',
      declGuardian: 'আমি এই ব্যক্তির পিতা/মাতা বা আইনসম্মত অভিভাবক',
      declRequired: 'দুটি বিবৃতিই আবশ্যক।',
      guardianDone: 'ধন্যবাদ। পিতামাতা বা অভিভাবকের যাচাই হওয়ার পরে পছন্দগুলি নথিভুক্ত হবে।',
      resend: 'নতুন কোড পাঠান',
      contactBy: 'আমার সঙ্গে যোগাযোগ করুন',
      ch_email: 'ইমেল',
      ch_sms: 'এসএমএস',
      ch_whatsapp: 'WhatsApp',
      ch_phone: 'ফোন কল',
      ch_post: 'ডাক', ch_push: 'অ্যাপ বিজ্ঞপ্তি',
    },
    // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
    kn: {
      chooseTitle: 'ನೀವು ಯಾವುದಕ್ಕೆ ಅನುಮತಿ ನೀಡುತ್ತೀರಿ ಎಂಬುದನ್ನು ಆಯ್ಕೆಮಾಡಿ',
      readNotice: 'ಪೂರ್ಣ ಸೂಚನೆಯನ್ನು ಓದಿ',
      required: 'ಸೇವೆಗೆ ಅಗತ್ಯ',
      optional: 'ಐಚ್ಛಿಕ: ಬೇಡವೆಂದರೆ ನಿಮ್ಮ ಸೇವೆಯಲ್ಲಿ ಯಾವುದೇ ಬದಲಾವಣೆ ಇಲ್ಲ',
      confirm: 'ನನ್ನ ಆಯ್ಕೆಗಳನ್ನು ದೃಢೀಕರಿಸಿ',
      acceptAll: 'ಎಲ್ಲಕ್ಕೂ ಅನುಮತಿ ನೀಡಿ',
      rejectAll: 'ಯಾವುದಕ್ಕೂ ಅನುಮತಿ ನೀಡಬೇಡಿ',
      saved: 'ನಿಮ್ಮ ಆಯ್ಕೆಗಳನ್ನು ಉಳಿಸಲಾಗಿದೆ.',
      changeLater: 'ಇವುಗಳನ್ನು ಯಾವಾಗ ಬೇಕಾದರೂ ಬದಲಾಯಿಸಿ ಅಥವಾ ಹಿಂಪಡೆಯಿರಿ',
      error: 'ನಿಮ್ಮ ಆಯ್ಕೆಗಳನ್ನು ಉಳಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
      dataUsed: 'ಬಳಸುವ ಡೇಟಾ',
      keptFor: 'ಇರಿಸುವ ಅವಧಿ',
      untilWithdrawn: 'ನೀವು ಹಿಂಪಡೆಯುವವರೆಗೆ',
      sharedWith: 'ಇವರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಲಾಗುತ್ತದೆ',
      notShared: 'ಹಂಚಿಕೊಳ್ಳಲಾಗುವುದಿಲ್ಲ',
      days: 'ದಿನಗಳು',
      close: 'ಮುಚ್ಚಿ',
      language: 'ಭಾಷೆ',
      yourRights: 'ನಿಮ್ಮ ಹಕ್ಕುಗಳು',
      grievance: 'ಕುಂದುಕೊರತೆ ಅಧಿಕಾರಿ',
      board: 'ಡೇಟಾ ಸಂರಕ್ಷಣಾ ಮಂಡಳಿಗೆ ದೂರು',
      statedTitle: 'ಸಮ್ಮತಿ ಕೇಳದೆ ಸಂಸ್ಕರಿಸಲಾಗುತ್ತದೆ',
      statedTag: 'ಕಾನೂನಿನಿಂದ ಅನುಮತಿಸಲಾಗಿದೆ: ಯಾವುದೇ ಆಯ್ಕೆ ಕೇಳಲಾಗುವುದಿಲ್ಲ',
      ground: 'ಕಾನೂನು ಆಧಾರ',
      codeSent: 'ನಾವು {to} ಗೆ ಒಂದು ಕೋಡ್ ಕಳುಹಿಸಿದ್ದೇವೆ. ನಿಮ್ಮ ಆಯ್ಕೆಗಳನ್ನು ದೃಢೀಕರಿಸಲು ಅದನ್ನು ನಮೂದಿಸಿ.',
      codePlaceholder: '6 ಅಂಕಿಯ ಕೋಡ್',
      confirmCode: 'ದೃಢೀಕರಿಸಿ',
      codeExpires: 'ಕೋಡ್ {n} ನಿಮಿಷಗಳವರೆಗೆ ಮಾನ್ಯವಾಗಿರುತ್ತದೆ.',
      startOver: 'ಮತ್ತೆ ಪ್ರಾರಂಭಿಸಿ',
      ageQuestion: 'ಈ ಸಮ್ಮತಿ ಯಾರಿಗಾಗಿ?',
      ageAdult: 'ನನಗಾಗಿ, 18 ವರ್ಷ ಅಥವಾ ಹೆಚ್ಚು',
      ageChild: '18 ವರ್ಷದೊಳಗಿನ ಮಗುವಿಗಾಗಿ, ಪೋಷಕರು ಅಥವಾ ರಕ್ಷಕರಾಗಿ',
      ageDisability: 'ವಿಕಲಚೇತನ ವ್ಯಕ್ತಿಗಾಗಿ, ಕಾನೂನುಬದ್ಧ ರಕ್ಷಕರಾಗಿ',
      ageRequired: 'ಈ ಸಮ್ಮತಿ ಯಾರಿಗಾಗಿ ಎಂದು ತಿಳಿಸಿ.',
      guardianContact: 'ಪೋಷಕರು ಅಥವಾ ರಕ್ಷಕರ ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
      relationship: 'ಸಂಬಂಧ',
      relParent: 'ಪೋಷಕರು',
      relGuardian: 'ಕಾನೂನುಬದ್ಧ ರಕ್ಷಕರು',
      guardianRequired: 'ಪೋಷಕರು ಅಥವಾ ರಕ್ಷಕರ ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ.',
      notForChildren: 'ಮಕ್ಕಳಿಗೆ ಲಭ್ಯವಿಲ್ಲ',
      guardianCodeSent: 'ನಾವು {to} ಗೆ ಒಂದು ಕೋಡ್ ಕಳುಹಿಸಿದ್ದೇವೆ. ಪೋಷಕರು ಅಥವಾ ರಕ್ಷಕರು ಅದನ್ನು ಇಲ್ಲಿ ನಮೂದಿಸಬೇಕು.',
      declAdult: 'ನನ್ನ ವಯಸ್ಸು 18 ವರ್ಷ ಅಥವಾ ಹೆಚ್ಚು',
      declGuardian: 'ನಾನು ಈ ವ್ಯಕ್ತಿಯ ಪೋಷಕ ಅಥವಾ ಕಾನೂನುಬದ್ಧ ರಕ್ಷಕ',
      declRequired: 'ಎರಡೂ ಹೇಳಿಕೆಗಳು ಅಗತ್ಯ.',
      guardianDone: 'ಧನ್ಯವಾದಗಳು. ಪೋಷಕರು ಅಥವಾ ರಕ್ಷಕರನ್ನು ಪರಿಶೀಲಿಸಿದ ನಂತರ ಆಯ್ಕೆಗಳನ್ನು ದಾಖಲಿಸಲಾಗುತ್ತದೆ.',
      resend: 'ಹೊಸ ಕೋಡ್ ಕಳುಹಿಸಿ',
      contactBy: 'ನನ್ನನ್ನು ಸಂಪರ್ಕಿಸುವ ವಿಧಾನ',
      ch_email: 'ಇಮೇಲ್',
      ch_sms: 'ಎಸ್ಎಂಎಸ್',
      ch_whatsapp: 'WhatsApp',
      ch_phone: 'ಫೋನ್ ಕರೆ',
      ch_post: 'ಅಂಚೆ', ch_push: 'ಆ್ಯಪ್ ಅಧಿಸೂಚನೆ',
    },
    // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
    ml: {
      chooseTitle: 'നിങ്ങൾ എന്തിനെല്ലാം അനുമതി നൽകുന്നു എന്ന് തിരഞ്ഞെടുക്കുക',
      readNotice: 'പൂർണ്ണ അറിയിപ്പ് വായിക്കുക',
      required: 'സേവനത്തിന് ആവശ്യമാണ്',
      optional: 'ഐച്ഛികം: വേണ്ടെന്ന് പറഞ്ഞാൽ നിങ്ങളുടെ സേവനത്തിൽ മാറ്റമൊന്നുമില്ല',
      confirm: 'എന്റെ തിരഞ്ഞെടുപ്പുകൾ സ്ഥിരീകരിക്കുക',
      acceptAll: 'എല്ലാത്തിനും അനുമതി നൽകുക',
      rejectAll: 'ഒന്നിനും അനുമതി നൽകരുത്',
      saved: 'നിങ്ങളുടെ തിരഞ്ഞെടുപ്പുകൾ സംരക്ഷിച്ചു.',
      changeLater: 'ഇവ എപ്പോൾ വേണമെങ്കിലും മാറ്റുകയോ പിൻവലിക്കുകയോ ചെയ്യാം',
      error: 'നിങ്ങളുടെ തിരഞ്ഞെടുപ്പുകൾ സംരക്ഷിക്കാൻ കഴിഞ്ഞില്ല. ദയവായി വീണ്ടും ശ്രമിക്കുക.',
      dataUsed: 'ഉപയോഗിക്കുന്ന ഡാറ്റ',
      keptFor: 'സൂക്ഷിക്കുന്ന കാലയളവ്',
      untilWithdrawn: 'നിങ്ങൾ പിൻവലിക്കുന്നതുവരെ',
      sharedWith: 'ഇവരുമായി പങ്കിടുന്നു',
      notShared: 'പങ്കിടുന്നില്ല',
      days: 'ദിവസം',
      close: 'അടയ്ക്കുക',
      language: 'ഭാഷ',
      yourRights: 'നിങ്ങളുടെ അവകാശങ്ങൾ',
      grievance: 'പരാതി പരിഹാര ഓഫീസർ',
      board: 'ഡാറ്റ സംരക്ഷണ ബോർഡിൽ പരാതി',
      statedTitle: 'സമ്മതം ചോദിക്കാതെ പ്രോസസ്സ് ചെയ്യുന്നു',
      statedTag: 'നിയമം അനുവദിക്കുന്നു: തിരഞ്ഞെടുപ്പ് ചോദിക്കുന്നില്ല',
      ground: 'നിയമപരമായ അടിസ്ഥാനം',
      codeSent: '{to} എന്നതിലേക്ക് ഞങ്ങൾ ഒരു കോഡ് അയച്ചു. നിങ്ങളുടെ തിരഞ്ഞെടുപ്പുകൾ സ്ഥിരീകരിക്കാൻ അത് നൽകുക.',
      codePlaceholder: '6 അക്ക കോഡ്',
      confirmCode: 'സ്ഥിരീകരിക്കുക',
      codeExpires: 'കോഡിന് {n} മിനിറ്റ് സാധുതയുണ്ട്.',
      startOver: 'വീണ്ടും ആരംഭിക്കുക',
      ageQuestion: 'ഈ സമ്മതം ആർക്കുവേണ്ടിയാണ്?',
      ageAdult: 'എനിക്കുവേണ്ടി, 18 വയസ്സോ അതിൽ കൂടുതലോ',
      ageChild: '18 വയസ്സിൽ താഴെയുള്ള കുട്ടിക്കുവേണ്ടി, മാതാപിതാക്കൾ അല്ലെങ്കിൽ രക്ഷിതാവ് എന്ന നിലയിൽ',
      ageDisability: 'ഭിന്നശേഷിയുള്ള വ്യക്തിക്കുവേണ്ടി, നിയമപരമായ രക്ഷിതാവ് എന്ന നിലയിൽ',
      ageRequired: 'ഈ സമ്മതം ആർക്കുവേണ്ടിയാണെന്ന് വ്യക്തമാക്കുക.',
      guardianContact: 'മാതാപിതാക്കളുടെയോ രക്ഷിതാവിന്റെയോ ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ നമ്പർ',
      relationship: 'ബന്ധം',
      relParent: 'മാതാപിതാക്കൾ',
      relGuardian: 'നിയമപരമായ രക്ഷിതാവ്',
      guardianRequired: 'മാതാപിതാക്കളുടെയോ രക്ഷിതാവിന്റെയോ ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ നമ്പർ നൽകുക.',
      notForChildren: 'കുട്ടികൾക്ക് ലഭ്യമല്ല',
      guardianCodeSent: '{to} എന്നതിലേക്ക് ഞങ്ങൾ ഒരു കോഡ് അയച്ചു. മാതാപിതാക്കളോ രക്ഷിതാവോ അത് ഇവിടെ നൽകണം.',
      declAdult: 'എനിക്ക് 18 വയസ്സോ അതിൽ കൂടുതലോ ഉണ്ട്',
      declGuardian: 'ഞാൻ ഈ വ്യക്തിയുടെ മാതാവ്/പിതാവ് അല്ലെങ്കിൽ നിയമപരമായ രക്ഷിതാവ് ആണ്',
      declRequired: 'രണ്ട് പ്രസ്താവനകളും ആവശ്യമാണ്.',
      guardianDone: 'നന്ദി. മാതാപിതാക്കളെയോ രക്ഷിതാവിനെയോ പരിശോധിച്ച ശേഷം തിരഞ്ഞെടുപ്പുകൾ രേഖപ്പെടുത്തും.',
      resend: 'പുതിയ കോഡ് അയയ്ക്കുക',
      contactBy: 'എന്നെ ബന്ധപ്പെടേണ്ട മാർഗം',
      ch_email: 'ഇമെയിൽ',
      ch_sms: 'എസ്എംഎസ്',
      ch_whatsapp: 'WhatsApp',
      ch_phone: 'ഫോൺ കോൾ',
      ch_post: 'തപാൽ', ch_push: 'ആപ്പ് അറിയിപ്പ്',
    },
  };

  var LANG_NAMES = {
    en: 'English', hi: 'हिन्दी', mr: 'मराठी', ta: 'தமிழ்', te: 'తెలుగు', bn: 'বাংলা',
    gu: 'ગુજરાતી', kn: 'ಕನ್ನಡ', ml: 'മലയാളം', or: 'ଓଡ଼ିଆ', pa: 'ਪੰਜਾਬੀ', as: 'অসমীয়া',
    ur: 'اردو', sa: 'संस्कृतम्', ne: 'नेपाली', sd: 'سنڌي', ks: 'کٲشُر', kok: 'कोंकणी',
    mai: 'मैथिली', doi: 'डोगरी', brx: 'बड़ो', mni: 'ꯃꯤꯇꯩ ꯂꯣꯟ', sat: 'ᱥᱟᱱᱛᱟᱲᱤ',
  };

  var CSS = [
    ':host{all:initial}',
    SHARED_CSS,
    /* The card, inline in the host page: a rail beside the body. */
    '.wk{display:grid;grid-template-columns:52px minmax(0,1fr);max-width:var(--wk-max-width,560px);',
    'font-family:var(--wk-font,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif);',
    'font-size:var(--wk-size,14px);line-height:1.5;letter-spacing:var(--wk-tracking,0);word-spacing:var(--wk-wordsp,0);',
    'color:var(--wk-text,#101828);background:var(--wk-surface,#fff);',
    'border:1px solid var(--wk-border,#D9DEE7);border-radius:var(--wk-radius,8px);overflow:hidden}',
    '.wk-tag{font-size:.78em;font-weight:600;letter-spacing:.04em}',
    '.wk-tag-req{color:var(--wk-warn,#946200)}',
    '.wk-tag-opt{color:var(--wk-subtle,#6B7280)}',
    '.wk-form{margin-top:10px}',
    '.wk-age{margin:10px 0 12px;padding:10px 12px;border:1px solid var(--wk-border,#D9DEE7);border-radius:6px}',
    '.wk-age legend{font-weight:600;padding:0 4px}',
    '.wk-age label{display:flex;gap:8px;align-items:flex-start;margin:6px 0;cursor:pointer}',
    '.wk-guardian{display:grid;gap:8px;margin-top:8px}',
    '.wk-guardian .wk-f-input,.wk-verify .wk-code{font:inherit;font-size:1em;width:100%;box-sizing:border-box;padding:9px 10px;border:1px solid var(--wk-border,#C9D0DC);border-radius:6px;background:#fff;color:inherit}',
    '.wk-cat.wk-blocked{opacity:.6}',
    '.wk-chans{display:flex;flex-wrap:wrap;gap:6px 14px;align-items:center;margin-top:8px;font-size:.92em}',
    '.wk-chans-label{color:var(--wk-muted,#6B7280)}',
    '.wk-chans label{display:inline-flex;gap:6px;align-items:center;cursor:pointer}',
    '.wk-chans.wk-off{opacity:.5}',
    '.wk-decl{display:flex;gap:8px;align-items:flex-start;margin:6px 0}',
    '.wk-verify{margin-top:14px;padding:12px;border:1px solid var(--wk-border,#D9DEE7);border-radius:6px}',
    '.wk-verify-text{margin:0 0 10px}',
    '.wk-verify-row{display:flex;gap:8px;align-items:stretch}',
    '.wk-verify-row .wk-code{flex:1;min-width:0;font-size:1.1em;letter-spacing:.2em}',
    '.wk-verify-note{margin:8px 0;font-size:.9em;color:var(--wk-subtle,#6B7280)}',
    /* Operator-built form fields. Deliberately plain: the form sits on the
       customer's own page and should not fight the page's own styling. */
    '.wk-f{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}',
    '.wk-f-heading{font-size:1.05em;font-weight:600;margin:18px 0 6px;color:var(--wk-text,#101828)}',
    '.wk-f-label{font-size:.92em;font-weight:500;color:var(--wk-text,#101828)}',
    '.wk-f-req{color:var(--wk-focus,#2563EB)}',
    '.wk-f input[type=text],.wk-f input[type=email],.wk-f input[type=tel],',
    '.wk-f input[type=date],.wk-f select,.wk-f textarea{font:inherit;font-size:1em;width:100%;',
    'padding:10px 11px;border-radius:6px;border:1px solid var(--wk-border,#D9DEE7);',
    'background:var(--wk-surface,#fff);color:var(--wk-text,#101828);box-sizing:border-box}',
    '.wk-f input:focus-visible,.wk-f select:focus-visible,.wk-f textarea:focus-visible{outline:2px solid var(--wk-focus,#2563EB);outline-offset:2px}',
    '.wk-f textarea{resize:vertical;min-height:72px}',
    '.wk-f-help{font-size:.86em;color:var(--wk-muted,#6B7280);line-height:1.45}',
    '.wk-f-err{font-size:.86em;color:#B3261E}',
    '.wk-f-bad input,.wk-f-bad select,.wk-f-bad textarea{border-color:#B3261E}',
    '.wk-cat.wk-f-bad .wk-cat-name{color:#B3261E}',
    '.wk-modal{position:fixed;inset:0;background:rgba(16,24,40,.55);display:flex;align-items:center;',
    'justify-content:center;padding:20px;z-index:2147483647}',
    '.wk-modal-card{background:var(--wk-surface,#fff);border-radius:10px;max-width:640px;width:100%;',
    'max-height:82vh;overflow:auto;padding:24px;font-size:var(--wk-size,14px)}',
    '.wk-modal-card h3{margin:18px 0 6px;font-size:1.05em}',
    '.wk-modal-card h2{margin:0 0 10px;font-size:1.25em}',
    '.wk-modal-card ul{margin:6px 0;padding-left:20px}',
    '.wk-modal-card table{width:100%;border-collapse:collapse;font-size:.92em;margin-top:8px}',
    '.wk-modal-card th,.wk-modal-card td{border:1px solid var(--wk-border,#D9DEE7);padding:6px 8px;text-align:left;vertical-align:top}',
    /* Inline layout: part of the host's form. */
    '.wk.wk-inline{display:block;max-width:none;border:0;border-radius:0;background:transparent;overflow:visible}',
    '.wk-inline .wk-inline-head{display:flex;align-items:center;justify-content:space-between;gap:12px}',
    '.wk-inline .wk-eyebrow{padding:0}',
    '.wk-inline .wk-tools{flex-direction:row;background:transparent;padding:0;gap:2px;color:var(--wk-subtle,#6B7280)}',
    '.wk-inline .wk-tools .wk-rail-mark{display:none}',
    '.wk-inline .wk-tools .wk-icon{width:30px;height:30px;color:var(--wk-subtle,#6B7280);opacity:1}',
    '.wk-inline .wk-tools .wk-icon:hover{background:var(--wk-tint,#EAF1FF);color:var(--wk-brand,#1C3D73)}',
    '.wk-inline .wk-tools .wk-icon[aria-expanded="true"]{background:var(--wk-brand,#1C3D73);color:#fff}',
    '.wk-inline .wk-tools .wk-icon:focus-visible{outline-color:var(--wk-focus,#2563EB);outline-offset:2px}',
    '.wk-inline .wk-strip{margin:8px 0 0}',
    '.wk-inline .wk-body{padding:6px 0 0}',
    '.wk-inline .wk-title{font-size:1.1em;margin-top:4px}',
    '.wk-inline .wk-actions{padding:12px 0 0}',
    '.wk-inline .wk-actions-empty{display:none}',
    '.wk-inline .wk-alert{margin:10px 0 0}',
    '.wk-inline .wk-foot{padding:10px 0 0}',
    /* Ledger rows, second cut: name and switch on one line, one-word tag, two
       facts with small marks, itemised data behind a disclosure. */
    '.wk-cat-row-v2{grid-template-columns:minmax(0,1fr) auto;gap:0 16px;padding:14px 0 12px;align-items:start}',
    '.wk-cat-main{min-width:0;display:flex;flex-direction:column;gap:5px}',
    '.wk-cat-head{display:flex;align-items:center;flex-wrap:wrap;gap:4px 8px;min-height:24px}',
    '.wk-cat-row-v2 .wk-cat-name{font-weight:600;font-size:1em;line-height:1.35;display:inline;cursor:pointer}',
    '.wk-stated .wk-cat-name{cursor:default}',
    '.wk-cat-row-v2 .wk-cat-desc{margin:0;color:var(--wk-muted,#4B5563);font-size:.92em;line-height:1.5}',
    '.wk-chip{display:inline-flex;align-items:center;font-size:.72em;font-weight:600;letter-spacing:.04em;text-transform:uppercase;line-height:1;padding:4px 7px;border-radius:4px;white-space:nowrap}',
    '.wk-chip-req{color:var(--wk-warn,#946200);background:var(--wk-warn-bg,#FFF6E0)}',
    '.wk-chip-opt{color:var(--wk-subtle,#6B7280);background:var(--wk-card,#F1F3F7)}',
    '.wk-facts{margin-top:2px;gap:4px 14px}',
    '.wk-items{list-style:none;margin:8px 0 0;padding:0;display:flex;flex-wrap:wrap;gap:6px}',
    '.wk-item{display:inline-flex;align-items:center;gap:6px;font-size:.82em;line-height:1.3;border:1px solid var(--wk-border,#E4E8EF);border-radius:999px;padding:3px 4px 3px 9px;color:var(--wk-text,#101828)}',
    '.wk-sens{font-size:.86em;font-weight:600;border-radius:999px;padding:1px 7px;background:#EEF2F6;color:#475467}',
    '.wk-item-high .wk-sens{background:#FEF3F2;color:#B42318}.wk-item-medium .wk-sens{background:#FFF6E5;color:#93520A}.wk-item-low .wk-sens{background:#ECFDF3;color:#067647}',
    '.wk-fact{display:inline-flex;align-items:center;gap:5px;white-space:nowrap;color:var(--wk-subtle,#6B7280);font-size:1em}',
    '.wk-fact svg{width:13px;height:13px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round;flex:none}',
    '.wk-cat-more{margin-top:2px}',
    '.wk-cat-more .wk-more{font-size:.84em;text-decoration:none;color:var(--wk-brand,#1C3D73);font-weight:600}',
    '.wk-cat-more .wk-more:hover{text-decoration:underline}',
    '.wk-detail{margin:4px 0 0;padding:10px 12px;border-radius:6px;background:var(--wk-card,#F8F9FB);border:1px solid var(--wk-border,#E4E8EF);display:grid;grid-template-columns:max-content minmax(0,1fr);gap:4px 12px;font-size:.86em}',
    '.wk-detail dt{color:var(--wk-subtle,#6B7280);font-weight:500}',
    '.wk-detail dd{margin:0;color:var(--wk-text,#101828)}',
    '.wk-detail .wk-detail-note{grid-column:1/-1;color:var(--wk-subtle,#6B7280);padding-top:2px}',
    '.wk-detail[hidden]{display:none}',
    '.wk-cat-ctl{display:flex;align-items:flex-start;padding-top:0}',
    '.wk-cat-ctl .wk-switch{margin-top:0}',
    '.wk-cat-ctl .wk-always{padding-top:3px}',
    /* Confirmation after a save: the ledger record and its receipt. */
    '.wk-done{grid-column:1/-1;padding:20px 22px;min-width:0}',
    '.wk-done-head{display:flex;gap:10px;align-items:center}',
    '.wk-done-head svg{width:26px;height:26px;flex:none;fill:none;stroke:#fff;stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round;background:var(--wk-ok,#0F766E);border-radius:50%;padding:4px;box-sizing:border-box}',
    '.wk-done-head h2{margin:0;font-size:1.2em}',
    '.wk-done-line{margin:8px 0 14px;color:var(--wk-muted,#4B5563)}',
    '.wk-rec{border:1px solid var(--wk-border,#D9DEE7);border-radius:8px;padding:12px 14px;background:var(--wk-card,#F8F9FB)}',
    '.wk-rec h3{margin:0 0 8px;font-size:.95em}',
    '.wk-rec dl{display:grid;grid-template-columns:max-content minmax(0,1fr);gap:4px 14px;margin:0;font-size:.92em}',
    '.wk-rec dt{color:var(--wk-subtle,#6B7280)}',
    '.wk-rec dd{margin:0;min-width:0}',
    '.wk-mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:.92em}',
    '.wk-rec ul{margin:8px 0 0;padding-left:18px;font-size:.88em;color:var(--wk-muted,#4B5563)}',
    '.wk-copy{font:inherit;font-size:.85em;margin-left:8px;padding:1px 8px;border:1px solid var(--wk-border,#C9D0DC);border-radius:4px;background:#fff;cursor:pointer;color:var(--wk-brand,#1C3D73)}',
    '.wk-done-acts{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}',
    '.wk-done-acts a.wk-btn{text-decoration:none;display:inline-flex;align-items:center;justify-content:center}',
    '.wk-done-note{font-size:.84em;color:var(--wk-subtle,#6B7280);margin:10px 0 0}',
    '.wk-done .wk-btn-primary{margin-top:14px;width:100%}',
    '@media (max-width:480px){.wk{grid-template-columns:44px minmax(0,1fr)}.wk-pair{flex-direction:column}.wk-btn{white-space:normal}.wk-facts{flex-direction:row}.wk-facts .wk-sep{display:none}}',
  ].join('');

  function h(tag, attrs, children) {
    var el = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        var v = attrs[k];
        if (v === null || v === undefined || v === false) continue;
        if (k === 'class') el.className = v;
        else if (k === 'text') el.textContent = v;
        else if (k.indexOf('on') === 0) el.addEventListener(k.slice(2).toLowerCase(), v);
        else el.setAttribute(k, v === true ? '' : String(v));
      }
    }
    (children || []).forEach(function (c) {
      if (c === null || c === undefined || c === false) return;
      el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return el;
  }

  function t(lang, key) {
    return (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.en[key] || key;
  }

  function Widget(options) {
    this.values = {};
    this.errors = {};
    this.api = String(options.api || '').replace(/\/$/, '');
    this.key = options.key;
    this.lang = options.lang || 'en';
    this.langGiven = !!options.lang;
    this.identifier = options.identifier || null;
    this.identifierType = options.identifierType || 'email';
    this.identifierField = options.identifierField || null;
    /**
     * 'card' (default): the rail, the eyebrow and a bordered card, for a widget
     * that stands beside the host's form. 'inline': no rail, no border, no
     * eyebrow; the purposes render as a section of the host's own form, and the
     * host's submit button records them.
     */
    this.layout = options.layout === 'inline' ? 'inline' : 'card';
    /** With a form: the host has its own submit, so the widget shows none. */
    this.hideSubmit = !!options.hideSubmit;
    this.target = options.target;
    this.autoSubmit = options.autoSubmit !== false;
    /** After a save, show the ledger record and receipt links. Off: a one-line message, as before. */
    this.confirmation = options.confirmation !== false;
    /** Show each purpose's data items with their sensitivity under the description. Off by default. */
    this.showSensitivity = !!options.showSensitivity;
    /** The public verify page. Defaults to the one this deployment serves beside the API. */
    this.verifyUrl = options.verifyUrl || (this.api + '/preferences/verify.html');
    /**
     * Where the config comes from. The console's preview passes its own loader
     * so it can render an unpublished draft — which means the preview is this
     * widget, not a second implementation of it that could drift from it.
     */
    this.loadConfig = options.loadConfig || null;
    /** Preview renders everything and submits nothing. */
    this.preview = !!options.preview;
    this.choices = {};
    this.config = null;
    this.host = null;
    this.root = null;
    this.a11y = null;
    this.strip = null;
    this.stripButton = null;
    this.stripSlot = null;
  }

  Widget.prototype.mount = function () {
    var target = typeof this.target === 'string' ? document.querySelector(this.target) : this.target;
    if (!target) throw new Error('Wekalp consent widget: target element not found');
    this.host = target;
    this.root = target.shadowRoot || target.attachShadow({ mode: 'open' });
    this.root.innerHTML = '';
    this.root.appendChild(h('style', { text: CSS }));
    this.container = h('div', { class: 'wk' + (this.layout === 'inline' ? ' wk-inline' : ''), role: 'group', 'aria-labelledby': 'wk-title' });
    this.root.appendChild(this.container);
    return this.load();
  };

  Widget.prototype.load = function () {
    var self = this;
    var source = this.loadConfig
      ? Promise.resolve(this.loadConfig(this.lang))
      : fetch(this.api + '/v1/widget/config?lang=' + encodeURIComponent(this.lang), {
        headers: { 'X-Consent-Key': this.key },
      }).then(function (r) {
        if (!r.ok) throw new Error('config request failed with ' + r.status);
        return r.json();
      });
    return source
      .then(function (config) {
        self.config = config;
        // A form may name the language to show when the page did not. One
        // reload, then the served language stands whatever it is.
        var wanted = config.form && config.form.settings && config.form.settings.defaultLang;
        if (!self.langGiven && wanted && wanted !== self.lang && !self._defaulted) {
          self._defaulted = true;
          self.lang = wanted;
          return self.load();
        }
        self.lang = config.notice.lang;
        // Nothing is pre-ticked: every purpose starts as an explicit "not yet".
        self.choices = {};
        config.purposes.forEach(function (p) { if (!isStated(p)) self.choices[p.purposeId] = false; });
        self.render();
        self.emit('ready', { config: config });
        return config;
      })
      .catch(function (err) {
        self.renderError(err);
        throw err;
      });
  };

  Widget.prototype.setLang = function (lang) {
    this.lang = lang;
    return this.load();
  };

  Widget.prototype.closeStrip = function () {
    if (this.strip) { this.strip.remove(); this.strip = null; }
    if (this.stripButton) { this.stripButton.setAttribute('aria-expanded', 'false'); this.stripButton = null; }
  };

  Widget.prototype.openStrip = function (button, node) {
    if (this.stripButton === button) { this.closeStrip(); return; }
    this.closeStrip();
    this.strip = node;
    this.stripButton = button;
    button.setAttribute('aria-expanded', 'true');
    this.stripSlot.appendChild(node);
    var first = node.querySelector('button,input');
    if (first) first.focus();
  };

  Widget.prototype.rail = function () {
    var self = this;
    var lang = this.lang;
    var cfg = this.config;
    var mark = h('span', { class: 'wk-rail-mark', title: dt(lang, 'consentLabel') });
    mark.appendChild(svg('shield'));
    var offered = (cfg.languages && cfg.languages.length)
      ? cfg.languages
      : cfg.notice.availableLangs.map(function (code) { return { code: code, endonym: LANG_NAMES[code] || code }; });
    return h('div', { class: 'wk-rail' }, [
      mark,
      railButton(h, 'person', dt(lang, 'accessibility'), function (e) {
        self.openStrip(e.currentTarget, sharedDisplayStrip(h, lang, self.a11y, function () { applyDisplay(self.container, self.a11y); }));
      }),
      offered.length > 1 ? railButton(h, 'lang', dt(lang, 'language'), function (e) {
        self.openStrip(e.currentTarget, sharedLangStrip(h, lang, offered, function (code) { self.setLang(code); }));
      }) : null,
    ]);
  };

  /** 1095 days reads as 3 years; 180 days as 6 months; 45 days stays 45 days. */
  function fmtRetention(lang, days) {
    if (!days) return t(lang, 'kept') + ' ' + t(lang, 'untilWithdrawn');
    if (days % 365 === 0) { var y = days / 365; return t(lang, 'kept') + ' ' + y + ' ' + t(lang, y === 1 ? 'year' : 'years'); }
    if (days >= 60 && days % 30 === 0) { var m = days / 30; return t(lang, 'kept') + ' ' + m + ' ' + t(lang, m === 1 ? 'month' : 'months'); }
    return t(lang, 'kept') + ' ' + days + ' ' + t(lang, 'days');
  }

  /**
   * One ledger row: a purpose that is asked, or one that is stated. The row
   * leads with the name, the switch and one word on whether the service needs
   * it; the description follows; retention and recipients are two short facts.
   * The itemised data and the named recipients sit behind "What we use", one
   * tap away, so the row reads at a glance and still shows everything.
   */
  Widget.prototype.purposeRow = function (p, opts) {
    var self = this;
    var lang = this.lang;
    opts = opts || {};
    var stated = isStated(p);
    var inputId = opts.inputId || ('wk-' + p.purposeId);
    var processors = p.processors || [];
    var recipients = processors.length === 0 ? t(lang, 'notShared')
      : processors.length === 1 ? t(lang, 'recipientOne') : t(lang, 'recipientMany').replace('{n}', String(processors.length));
    // S.9(3): when the consent is for a child, a purpose not offered to
    // children stays off and cannot be switched on.
    var blocked = !stated && self.age && self.age !== 'adult' && self.childRule(p.purposeId) === 'not_for_children';

    // The one-word tag beside the name. Its id is what the switch is described by.
    var tag;
    if (stated) tag = null;
    else if (blocked) tag = h('span', { class: 'wk-chip wk-chip-opt', id: inputId + '-tag', text: t(lang, 'notForChildren') });
    else tag = h('span', {
      class: 'wk-chip ' + (p.requiredForService ? 'wk-chip-req' : 'wk-chip-opt'), id: inputId + '-tag',
      title: p.requiredForService ? t(lang, 'required') : t(lang, 'optional'),
      text: p.requiredForService ? t(lang, 'requiredShort') : t(lang, 'optionalShort'),
    });

    var facts = h('div', { class: 'wk-cat-meta wk-facts' }, [
      h('span', { class: 'wk-fact' }, [svg('clock'), document.createTextNode(fmtRetention(lang, p.retentionDays))]),
      h('span', { class: 'wk-fact' }, [svg('share'), document.createTextNode(recipients)]),
      stated ? h('span', { class: 'wk-fact' }, [svg('scale'), document.createTextNode(t(lang, 'ground') + ': ' + (p.basisSubtype || 'S.7'))]) : null,
    ]);

    // With data-show-sensitivity: each data item and its sensitivity, in view.
    var SENS = { low: 'sensLow', medium: 'sensMedium', high: 'sensHigh' };
    var items = self.showSensitivity && p.dataItems && p.dataItems.length
      ? h('ul', { class: 'wk-items', 'aria-label': t(lang, 'dataUsed') }, p.dataItems.map(function (it) {
        var word = t(lang, SENS[it.sensitivity] || 'sensLow');
        return h('li', { class: 'wk-item wk-item-' + (SENS[it.sensitivity] ? it.sensitivity : 'low'), title: t(lang, 'sensitivity') + ': ' + word }, [
          document.createTextNode(it.name), h('span', { class: 'wk-sens', text: word }),
        ]);
      }))
      : null;

    // Everything the notice itemises for this purpose, one tap away.
    var detail = h('dl', { class: 'wk-detail', hidden: true, id: inputId + '-detail' }, [
      h('dt', { text: t(lang, 'dataUsed') }), h('dd', { text: (p.dataCategories || []).join(', ') || '—' }),
      h('dt', { text: t(lang, 'sharedWith') }), h('dd', { text: processors.length ? processors.join(', ') : t(lang, 'notShared') }),
      !stated && !p.requiredForService ? h('dd', { class: 'wk-detail-note', text: t(lang, 'optional') }) : null,
      !stated && p.requiredForService ? h('dd', { class: 'wk-detail-note', text: t(lang, 'required') }) : null,
    ]);
    var more = h('button', { class: 'wk-more', type: 'button', 'aria-expanded': 'false', 'aria-controls': inputId + '-detail' }, [
      document.createTextNode(t(lang, 'details')), svg('chevron'),
    ]);
    more.onclick = function () {
      var open = more.getAttribute('aria-expanded') === 'true';
      more.setAttribute('aria-expanded', open ? 'false' : 'true');
      detail.hidden = open;
      more.firstChild.textContent = open ? t(lang, 'details') : t(lang, 'hideDetails');
    };

    var control = stated
      ? alwaysOn(h, lang, t(lang, 'statedTag'))
      : h('span', { class: 'wk-switch' }, [
          h('input', {
            type: 'checkbox', id: inputId, 'aria-describedby': inputId + '-tag',
            checked: !blocked && self.choices[p.purposeId] ? true : null,
            disabled: blocked ? true : null,
            onchange: function (e) {
              self.choices[p.purposeId] = e.target.checked;
              self.syncChannels(p.purposeId);
              if (opts.onChange) opts.onChange(e.target.checked);
              self.emit('change', { purposeId: p.purposeId, granted: e.target.checked });
            },
          }),
          h('span', { class: 'wk-track' }), h('span', { class: 'wk-knob' }),
        ]);

    var desc = opts.help !== undefined ? opts.help : p.description;
    return h('div', { class: 'wk-cat' + (opts.error ? ' wk-f-bad' : '') + (blocked ? ' wk-blocked' : '') + (stated ? ' wk-stated' : '') }, [
      h('div', { class: 'wk-cat-row wk-cat-row-v2' }, [
        h('div', { class: 'wk-cat-main' }, [
          h('div', { class: 'wk-cat-head' }, [
            h('label', { class: 'wk-cat-name', for: stated ? null : inputId, text: opts.label || p.name }),
            tag,
          ]),
          desc ? h('p', { class: 'wk-cat-desc', text: desc }) : null,
          facts,
          items,
          h('div', { class: 'wk-cat-more' }, [more]),
          detail,
          !stated && !blocked && p.channels && p.channels.length ? self.channelRow(p) : null,
          opts.error ? h('span', { class: 'wk-f-err', text: opts.error }) : null,
        ]),
        h('div', { class: 'wk-cat-ctl' }, [control]),
      ]),
    ]);
  };

  Widget.prototype.render = function () {
    var self = this;
    var cfg = this.config;
    var lang = this.lang;
    var c = this.container;
    c.innerHTML = '';
    this.closeStrip();
    this.a11y = this.a11y || loadA11y();
    c.setAttribute('dir', (cfg.notice && cfg.notice.dir) || 'ltr');
    c.setAttribute('lang', lang);
    // The notice's font stack is the tenant default; a host page that sets
    // --wk-font on the mount point has themed the widget on purpose and wins.
    var hostFont = this.host && getComputedStyle(this.host).getPropertyValue('--wk-font').trim();
    if (cfg.notice && cfg.notice.fontStack && !hostFont) c.style.setProperty('--wk-font', cfg.notice.fontStack);

    this.stripSlot = h('div');
    this.alertSlot = h('div');

    var eyebrow = h('div', { class: 'wk-eyebrow' }, [
      h('span', { text: dt(lang, 'consentLabel') }),
      h('span', { text: '/' }),
      h('b', { text: cfg.tenantName || '' }),
    ]);

    var body = h('div', { class: 'wk-body' }, [
      h('h2', { class: 'wk-title', id: 'wk-title', text: t(lang, 'chooseTitle') }),
      h('p', { class: 'wk-intro' }, [
        h('button', { class: 'wk-link', type: 'button', text: t(lang, 'readNotice'), onclick: function () { self.openNotice(); } }),
      ]),
    ]);

    if (this.asksAge()) body.appendChild(this.renderAge());

    if (cfg.form) {
      var formBox = h('div', { class: 'wk-form' });
      this.renderFormFields(formBox);
      body.appendChild(formBox);
      var statedRows = cfg.purposes.filter(isStated).map(function (p) { return self.purposeRow(p); });
      if (statedRows.length) {
        body.appendChild(h('p', { class: 'wk-note', text: t(lang, 'statedTitle') }));
        body.appendChild(h('div', { class: 'wk-cats' }, statedRows));
      }
    } else {
      var rows = this.askedPurposes().map(function (p) { return self.purposeRow(p); })
        .concat(cfg.purposes.filter(isStated).map(function (p) { return self.purposeRow(p); }));
      body.appendChild(h('div', { class: 'wk-cats' }, rows));
    }

    // Equal weight: the "allow none" path is exactly as easy as "allow all".
    // A form carries its own submit (and the operator's own wording for it), so
    // the bulk toggles would be answering questions the form did not ask.
    var actions;
    if (cfg.form && this.hideSubmit) {
      actions = h('div', { class: 'wk-actions wk-actions-empty' });
    } else if (cfg.form) {
      var submit = h('button', {
        class: 'wk-btn wk-btn-primary', type: 'button',
        text: cfg.form.settings.submitText,
        style: 'background:' + cfg.form.styling.buttonBackground
          + ';border-color:' + cfg.form.styling.buttonBackground
          + ';color:' + cfg.form.styling.buttonText,
        onclick: function (e) { self.submit({ button: e.currentTarget }); },
      });
      actions = h('div', { class: 'wk-actions' }, [
        submit,
        cfg.form.settings.showReset ? h('button', {
          class: 'wk-textact', type: 'button', text: cfg.form.settings.resetText,
          onclick: function () { self.values = {}; self.errors = {}; self.render(); },
        }) : null,
      ]);
    } else {
      actions = h('div', { class: 'wk-actions' }, [
        h('button', {
          class: 'wk-btn wk-btn-primary', type: 'button', text: t(lang, 'confirm'),
          onclick: function (e) { self.submit({ button: e.currentTarget }); },
        }),
        h('div', { class: 'wk-pair' }, [
          h('button', { class: 'wk-btn', type: 'button', text: t(lang, 'rejectAll'), onclick: function () { self.setAll(false); } }),
          h('button', { class: 'wk-btn', type: 'button', text: t(lang, 'acceptAll'), onclick: function () { self.setAll(true); } }),
        ]),
      ]);
    }

    this.actionsSlot = actions;

    var foot = h('div', { class: 'wk-foot' }, [
      cfg.preferenceCentreUrl
        ? h('a', { class: 'wk-link', href: cfg.preferenceCentreUrl, target: '_blank', rel: 'noopener', text: t(lang, 'changeLater') })
        : h('span'),
      poweredBy(h, lang),
    ]);

    if (this.layout === 'inline') {
      // The host page owns the frame; the rail's two tools move into a quiet
      // line above the title so nothing is lost.
      var tools = this.rail();
      tools.className = 'wk-tools';
      c.appendChild(h('div', { class: 'wk-main' }, [h('div', { class: 'wk-inline-head' }, [eyebrow, tools]), this.stripSlot, body, actions, this.alertSlot, foot]));
    } else {
      c.appendChild(this.rail());
      c.appendChild(h('div', { class: 'wk-main' }, [eyebrow, this.stripSlot, body, actions, this.alertSlot, foot]));
    }
    applyDisplay(c, this.a11y);
  };

  /**
   * Renders the operator's own form: their fields, their wording, their order.
   * Consent checkboxes are the same thing as the toggles above — each one names
   * a purpose — so a form both collects details and records decisions in one
   * submission.
   */
  Widget.prototype.renderFormFields = function (c) {
    var self = this;
    var form = this.config.form;
    var jurisdiction = this.values[this.jurisdictionKey()] || '';

    form.fields.forEach(function (f) {
      // A field limited to other jurisdictions is not rendered at all, so
      // nobody answers a question that was never meant for them.
      if (f.showForJurisdictions.length && f.showForJurisdictions.indexOf(jurisdiction) === -1) return;

      var id = 'wk-f-' + f.key;
      var err = self.errors[f.key];

      // The host page already asks for the identifier (data-identifier-field):
      // asking twice is the thing a merged form must not do. The field stays
      // in the answers, filled from the host's input at submit time.
      if (f.identifier && self.identifierField) {
        self.identifierKey = f.key;
        if (err) c.appendChild(h('span', { class: 'wk-f-err', text: err }));
        return;
      }

      if (f.kind === 'heading') {
        c.appendChild(h('h3', { class: 'wk-f-heading', text: f.label }));
        return;
      }

      if (f.kind === 'checkbox' && f.purposeId && self.config.purposes.some(function (p) {
        return p.purposeId === f.purposeId && isStated(p);
      })) {
        return;
      }

      if (f.kind === 'checkbox' && f.purposeId) {
        var purpose = self.config.purposes.filter(function (p) { return p.purposeId === f.purposeId; })[0];
        if (purpose) {
          if (!c.lastChild || !c.lastChild.classList || !c.lastChild.classList.contains('wk-cats')) {
            c.appendChild(h('div', { class: 'wk-cats' }));
          }
          c.lastChild.appendChild(self.purposeRow(purpose, {
            inputId: id, label: f.label, help: f.help, error: err,
            onChange: function (granted) { self.values[f.key] = granted; },
          }));
          return;
        }
      }

      if (f.kind === 'checkbox') {
        c.appendChild(h('div', { class: 'wk-f wk-f-check' + (err ? ' wk-f-bad' : '') }, [
          h('label', { class: 'wk-f-checkrow', for: id }, [
            h('input', {
              type: 'checkbox', id: id, checked: !!self.values[f.key],
              onchange: function (e) {
                self.values[f.key] = e.target.checked;
                if (f.purposeId) {
                  self.choices[f.purposeId] = e.target.checked;
                  self.emit('change', { purposeId: f.purposeId, granted: e.target.checked });
                }
              },
            }),
            h('span', { class: 'wk-f-checktext' }, [
              h('span', { text: f.label }),
              f.help ? h('span', { class: 'wk-f-help', text: f.help }) : null,
            ]),
          ]),
          err ? h('span', { class: 'wk-f-err', text: err }) : null,
        ]));
        return;
      }

      var input;
      if (f.kind === 'paragraph') {
        input = h('textarea', {
          id: id, rows: '3', placeholder: f.placeholder, value: self.values[f.key] || '',
          oninput: function (e) { self.values[f.key] = e.target.value; },
        });
      } else if (f.kind === 'dropdown' || f.kind === 'jurisdiction') {
        input = h('select', {
          id: id,
          onchange: function (e) {
            self.values[f.key] = e.target.value;
            // Jurisdiction decides which other fields exist, so the form is
            // redrawn rather than patched.
            if (f.kind === 'jurisdiction') self.render();
          },
        }, [h('option', { value: '', text: f.placeholder || '—' })].concat(
          f.options.map(function (o) {
            return h('option', { value: o.value, text: o.label, selected: self.values[f.key] === o.value });
          }),
        ));
      } else {
        input = h('input', {
          type: f.kind === 'email' ? 'email' : f.kind === 'phone' ? 'tel' : f.kind === 'date' ? 'date' : 'text',
          id: id, placeholder: f.placeholder, value: self.values[f.key] || '',
          oninput: function (e) { self.values[f.key] = e.target.value; },
        });
      }

      c.appendChild(h('div', { class: 'wk-f' + (err ? ' wk-f-bad' : '') }, [
        f.showLabel
          ? h('label', { class: 'wk-f-label', for: id }, [
            h('span', { text: f.label }),
            f.required ? h('span', { class: 'wk-f-req', text: ' *' }) : null,
          ])
          : null,
        input,
        f.help ? h('span', { class: 'wk-f-help', text: f.help }) : null,
        err ? h('span', { class: 'wk-f-err', text: err }) : null,
      ]));
    });
  };

  Widget.prototype.jurisdictionKey = function () {
    var form = this.config && this.config.form;
    if (!form) return '__none__';
    for (var i = 0; i < form.fields.length; i += 1) {
      if (form.fields[i].kind === 'jurisdiction') return form.fields[i].key;
    }
    return '__none__';
  };

  /**
   * Client-side validation, for the person's sake. The server runs the same
   * rules again and is the one that decides — this only saves a round trip.
   */
  Widget.prototype.validateForm = function () {
    var self = this;
    var form = this.config.form;
    var jurisdiction = this.values[this.jurisdictionKey()] || '';
    this.errors = {};

    form.fields.forEach(function (f) {
      if (f.kind === 'heading' || f.kind === 'checkbox') return;
      if (f.showForJurisdictions.length && f.showForJurisdictions.indexOf(jurisdiction) === -1) return;

      var v = (self.values[f.key] || '').toString().trim();
      var msg = f.validation.message;
      if (!v) {
        if (f.required) self.errors[f.key] = msg || form.errors.required;
        return;
      }
      if (f.validation.minLength !== undefined && v.length < f.validation.minLength) {
        self.errors[f.key] = msg || form.errors.minLength;
      } else if (f.validation.maxLength !== undefined && v.length > f.validation.maxLength) {
        self.errors[f.key] = msg || form.errors.maxLength;
      } else if (f.validation.pattern && !new RegExp(f.validation.pattern).test(v)) {
        self.errors[f.key] = msg || form.errors.pattern;
      } else if (f.kind === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
        self.errors[f.key] = msg || form.errors.email;
      } else if (f.kind === 'phone' && !/^\+?[\d\s-]{10,15}$/.test(v)) {
        self.errors[f.key] = msg || form.errors.phone;
      }
    });

    return Object.keys(this.errors).length === 0;
  };

  /**
   * CS-2. A purpose processed under S.7 is stated, never asked: it has no
   * switch, takes no decision, and "allow all / allow none" pass over it.
   */
  function isStated(p) { return p.basis === 'legitimate_use'; }

  Widget.prototype.askedPurposes = function () {
    return this.config.purposes.filter(function (p) { return !isStated(p); });
  };

  Widget.prototype.setAll = function (value) {
    var self = this;
    var child = this.age && this.age !== 'adult';
    this.askedPurposes().forEach(function (p) {
      self.choices[p.purposeId] = value && !(child && self.childRule(p.purposeId) === 'not_for_children');
    });
    this.root.querySelectorAll('.wk-cat .wk-switch input').forEach(function (input) { if (!input.disabled) input.checked = value; });
    this.askedPurposes().forEach(function (p) { self.syncChannels(p.purposeId); });
    this.emit('change', { all: value });
  };

  Widget.prototype.readIdentifier = function (override) {
    if (override && override.identifier) {
      return { identifier: override.identifier, identifierType: override.identifierType || this.identifierType };
    }
    if (this.identifier) return { identifier: this.identifier, identifierType: this.identifierType };
    if (this.identifierField) {
      var field = document.querySelector(this.identifierField);
      if (field && field.value) return { identifier: field.value, identifierType: this.identifierType };
    }
    return null;
  };

  Widget.prototype.submit = function (opts) {
    var self = this;
    opts = opts || {};

    if (this.preview) {
      // Validation still runs, so the operator sees their own rules and
      // messages behave. Nothing is recorded.
      if (this.config.form && !this.validateForm()) {
        this.render();
        this.showAlert('err', 'Preview: these answers would be rejected.');
      } else {
        this.showAlert('ok', 'Preview. Nothing was recorded.');
      }
      return Promise.resolve({ preview: true });
    }

    var age = this.agePayload();
    if (age === false) return Promise.reject(new Error('age question unanswered'));

    // With a form, the identity is one of its own fields and the decisions are
    // its checkboxes; the server derives both from the answers.
    if (this.config.form) {
      if (this.identifierKey && this.identifierField) {
        var hostField = document.querySelector(this.identifierField);
        if (hostField) this.values[this.identifierKey] = hostField.value;
      }
      if (!this.validateForm()) {
        this.render();
        this.showAlert('err', t(this.lang, 'error'));
        return Promise.reject(new Error('some answers need fixing'));
      }
      return this.post(Object.assign({ form: { values: this.values } }, age ? { age: age } : {}), opts);
    }

    var principal = this.readIdentifier(opts);

    if (!principal) {
      // The host page has no identifier yet (typical on a signup form). Hand the
      // choices back so it can call submit() again once the account exists.
      this.emit('pending', { decisions: this.decisions() });
      return Promise.resolve({ pending: true, decisions: this.decisions() });
    }

    return this.post(Object.assign({ principal: principal, decisions: this.decisions() }, age ? { age: age } : {}), opts);
  };

  // ---------------------------------------------------------------- S.9

  Widget.prototype.asksAge = function () {
    var ch = this.config.children;
    return !!(ch && ch.ageQuestion === 'ask');
  };

  Widget.prototype.childRule = function (purposeId) {
    var ch = this.config.children;
    return (ch && ch.rules && ch.rules[purposeId]) || 'guardian';
  };

  /** Who the consent is for. A child's purposes not offered to children turn off. */
  Widget.prototype.renderAge = function () {
    var self = this;
    var lang = this.lang;
    var ch = this.config.children || {};
    var name = 'wk-age-' + this.renderId();
    var option = function (value, label) {
      var input = h('input', {
        type: 'radio', name: name, value: value,
        onchange: function () {
          self.age = value;
          if (value !== 'adult') {
            self.askedPurposes().forEach(function (p) {
              if (self.childRule(p.purposeId) === 'not_for_children') self.choices[p.purposeId] = false;
            });
          }
          self.render();
        },
      });
      if (self.age === value) input.checked = true;
      return h('label', {}, [input, h('span', { text: label })]);
    };
    var box = h('fieldset', { class: 'wk-age' }, [
      h('legend', { text: t(lang, 'ageQuestion') }),
      option('adult', t(lang, 'ageAdult')),
      option('child', t(lang, 'ageChild')),
      ch.disability ? option('disability', t(lang, 'ageDisability')) : null,
    ]);
    if (this.age && this.age !== 'adult') {
      this.guardian = this.guardian || { identifier: '', relationship: this.age === 'child' ? 'parent' : 'lawful_guardian' };
      var contact = h('input', {
        class: 'wk-f-input', type: 'text', autocomplete: 'off', value: this.guardian.identifier,
        'aria-label': t(lang, 'guardianContact'), placeholder: t(lang, 'guardianContact'),
        oninput: function (e) { self.guardian.identifier = e.target.value; },
      });
      var rel = h('select', {
        class: 'wk-f-input', 'aria-label': t(lang, 'relationship'),
        onchange: function (e) { self.guardian.relationship = e.target.value; },
      }, [
        h('option', { value: 'parent', text: t(lang, 'relParent') }),
        h('option', { value: 'lawful_guardian', text: t(lang, 'relGuardian') }),
      ]);
      rel.value = this.guardian.relationship;
      box.appendChild(h('div', { class: 'wk-guardian' }, [contact, rel]));
    }
    return box;
  };

  /** undefined when the point does not ask; false when unanswered; else the age block. */
  Widget.prototype.agePayload = function () {
    if (!this.asksAge()) return undefined;
    if (!this.age) { this.showAlert('err', t(this.lang, 'ageRequired')); return false; }
    if (this.age === 'adult') return { child: false };
    var g = this.guardian || {};
    var id = (g.identifier || '').trim();
    if (!id) { this.showAlert('err', t(this.lang, 'guardianRequired')); return false; }
    return {
      child: true,
      kind: this.age === 'disability' ? 'disability' : 'child',
      guardian: {
        identifier: id,
        identifierType: id.indexOf('@') > 0 ? 'email' : 'phone',
        relationship: g.relationship || 'parent',
      },
    };
  };

  /**
   * The guardian's step: the code sent to them and their two statements. The
   * choices are recorded later, after the organisation has made its check.
   */
  Widget.prototype.renderGuardianVerify = function (pending) {
    var self = this;
    var lang = this.lang;
    var input = h('input', {
      class: 'wk-f-input wk-code', type: 'text', inputmode: 'numeric', autocomplete: 'one-time-code',
      maxlength: '6', placeholder: t(lang, 'codePlaceholder'), 'aria-label': t(lang, 'codePlaceholder'),
    });
    var adult = h('input', { type: 'checkbox' });
    var guardian = h('input', { type: 'checkbox' });
    var button = h('button', {
      class: 'wk-btn wk-btn-primary', type: 'button', text: t(lang, 'confirmCode'),
      onclick: function () {
        var code = (input.value || '').trim();
        if (code.length < 4) { input.focus(); return; }
        if (!adult.checked || !guardian.checked) { self.showAlert('err', t(lang, 'declRequired')); return; }
        button.disabled = true;
        fetch(self.api + '/v1/guardian-consents/' + encodeURIComponent(pending.guardianConsentId) + '/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Consent-Key': self.key },
          body: JSON.stringify({ code: code, declaration: { isAdult: true, isParentOrGuardian: true } }),
        })
          .then(function (r) {
            return r.json().then(function (body) {
              if (!r.ok) throw new Error(body.message || 'confirmation failed');
              return body;
            });
          })
          .then(function (result) {
            self.showAlert('ok', t(lang, 'guardianDone'));
            self.emit('guardian', { guardianConsentId: pending.guardianConsentId, status: result.status });
            box.remove();
          })
          .catch(function (err) {
            self.showAlert('err', (err && err.message) || t(lang, 'error'));
            button.disabled = false;
            input.focus();
          });
      },
    });
    var resend = h('button', {
      class: 'wk-textact', type: 'button', text: t(lang, 'resend'),
      onclick: function () {
        fetch(self.api + '/v1/guardian-consents/' + encodeURIComponent(pending.guardianConsentId) + '/resend', {
          method: 'POST', headers: { 'X-Consent-Key': self.key },
        }).catch(function () {});
      },
    });
    var box = h('div', { class: 'wk-verify' }, [
      h('p', { class: 'wk-verify-text', text: t(lang, 'guardianCodeSent').replace('{to}', pending.to) }),
      h('label', { class: 'wk-decl' }, [adult, h('span', { text: t(lang, 'declAdult') })]),
      h('label', { class: 'wk-decl' }, [guardian, h('span', { text: t(lang, 'declGuardian') })]),
      h('div', { class: 'wk-verify-row' }, [input, button]),
      h('p', { class: 'wk-verify-note', text: t(lang, 'codeExpires').replace('{n}', String(pending.expiresInMinutes)) }),
      h('div', {}, [resend, h('button', { class: 'wk-textact', type: 'button', text: t(lang, 'startOver'), onclick: function () { self.render(); } })]),
    ]);
    if (this.actionsSlot && this.actionsSlot.parentNode) {
      this.actionsSlot.parentNode.replaceChild(box, this.actionsSlot);
    }
    this.actionsSlot = box;
    input.focus();
  };

  /**
   * The id the cookie banner (cookies.js) wrote into its first-party cookie on
   * this browser, when there is one. Sent with a capture so the person it
   * identifies is joined to that browser's cookie choices. The server hashes
   * it; the value itself is stored nowhere.
   */
  function visitorCookie() {
    var match = document.cookie.match(/(?:^|; )wk_visitor=([^;]*)/);
    var value = match ? decodeURIComponent(match[1]) : '';
    return /^vis_[a-z0-9]{8,40}$/.test(value) ? value : null;
  }

  /** One capture request, whichever path produced its payload. */
  Widget.prototype.post = function (payload, opts) {
    var self = this;
    if (opts.button) opts.button.disabled = true;
    var visitor = visitorCookie();
    return fetch(this.api + '/v1/consents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Consent-Key': this.key },
      body: JSON.stringify(Object.assign({
        lang: this.lang,
        evidence: { render_id: this.renderId(), page: location.pathname },
      }, visitor ? { visitorId: visitor } : {}, payload)),
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) {
            var e = new Error(body.message || 'capture failed');
            if (body.details && body.details.fields) e.fields = body.details.fields;
            throw e;
          }
          if (r.status === 202) body.__pending = true;
          return body;
        });
      })
      .then(function (receipt) {
        if (receipt.__pending) {
          delete receipt.__pending;
          if (receipt.pending === 'guardian') {
            self.renderGuardianVerify(receipt);
            self.emit('guardian', receipt);
            return receipt;
          }
          self.renderVerify(receipt);
          self.emit('verification', receipt);
          return receipt;
        }
        var cfg = self.config;
        if (!self.renderSaved(receipt)) self.showAlert('ok', (cfg.form && cfg.form.settings.successMessage) || t(self.lang, 'saved'));
        self.emit('submitted', receipt);
        return receipt;
      })
      .catch(function (err) {
        // The server validates independently; if it disagrees with the browser,
        // show what it said against the fields it named.
        if (err && err.fields) {
          self.errors = {};
          err.fields.forEach(function (f) { self.errors[f.field] = f.problem; });
          self.render();
        }
        self.showAlert('err', (err && err.message) || t(self.lang, 'error'));
        self.emit('error', { error: String(err && err.message) });
        throw err;
      })
      .finally(function () {
        if (opts.button) opts.button.disabled = false;
      });
  };

  /**
   * Double opt-in. The server parked the answers and sent a code; the actions
   * block becomes a code box until it comes back right. Nothing is recorded
   * before that, so "start over" simply returns to the form.
   */
  Widget.prototype.renderVerify = function (pending) {
    var self = this;
    var lang = this.lang;
    var cfg = this.config;
    var input = h('input', {
      class: 'wk-f-input wk-code', type: 'text', inputmode: 'numeric', autocomplete: 'one-time-code',
      maxlength: '6', placeholder: t(lang, 'codePlaceholder'), 'aria-label': t(lang, 'codePlaceholder'),
    });
    var button = h('button', {
      class: 'wk-btn wk-btn-primary', type: 'button', text: t(lang, 'confirmCode'),
      style: cfg.form ? 'background:' + cfg.form.styling.buttonBackground
        + ';border-color:' + cfg.form.styling.buttonBackground
        + ';color:' + cfg.form.styling.buttonText : '',
      onclick: function () {
        var code = (input.value || '').trim();
        if (code.length < 4) { input.focus(); return; }
        button.disabled = true;
        fetch(self.api + '/v1/consents/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Consent-Key': self.key },
          body: JSON.stringify({ challengeId: pending.challengeId, code: code }),
        })
          .then(function (r) {
            return r.json().then(function (body) {
              if (!r.ok) throw new Error(body.message || 'verification failed');
              return body;
            });
          })
          .then(function (receipt) {
            box.remove();
            if (!self.renderSaved(receipt)) self.showAlert('ok', (cfg.form && cfg.form.settings.successMessage) || t(lang, 'saved'));
            self.emit('submitted', receipt);
          })
          .catch(function (err) {
            self.showAlert('err', (err && err.message) || t(lang, 'error'));
            button.disabled = false;
            input.focus();
          });
      },
    });
    var box = h('div', { class: 'wk-verify' }, [
      h('p', { class: 'wk-verify-text', text: t(lang, 'codeSent').replace('{to}', pending.to) }),
      h('div', { class: 'wk-verify-row' }, [input, button]),
      h('p', { class: 'wk-verify-note', text: t(lang, 'codeExpires').replace('{n}', String(pending.expiresInMinutes)) }),
      h('button', { class: 'wk-textact', type: 'button', text: t(lang, 'startOver'), onclick: function () { self.render(); } }),
    ]);
    if (this.actionsSlot && this.actionsSlot.parentNode) {
      this.actionsSlot.parentNode.replaceChild(box, this.actionsSlot);
    }
    this.actionsSlot = box;
    input.focus();
  };

  Widget.prototype.decisions = function () {
    var self = this;
    return this.askedPurposes().map(function (p) {
      var d = { purposeId: p.purposeId, granted: !!self.choices[p.purposeId] };
      if (p.channels && p.channels.length && d.granted) d.channels = self.channelChoice(p);
      return d;
    });
  };

  /** Channels default to all on; the person switches off the ones they do not want. */
  Widget.prototype.channelChoice = function (p) {
    this.channels = this.channels || {};
    if (!this.channels[p.purposeId]) {
      var all = {};
      p.channels.forEach(function (c) { all[c] = true; });
      this.channels[p.purposeId] = all;
    }
    return this.channels[p.purposeId];
  };

  Widget.prototype.channelRow = function (p) {
    var self = this;
    var lang = this.lang;
    var choice = this.channelChoice(p);
    var on = !!this.choices[p.purposeId];
    return h('div', { class: 'wk-chans' + (on ? '' : ' wk-off'), 'data-purpose': p.purposeId }, [
      h('span', { class: 'wk-chans-label', text: t(lang, 'contactBy') }),
    ].concat(p.channels.map(function (c) {
      var input = h('input', {
        type: 'checkbox', checked: choice[c] ? true : null, disabled: on ? null : true,
        onchange: function (e) { choice[c] = e.target.checked; self.emit('change', { purposeId: p.purposeId, channel: c, allowed: e.target.checked }); },
      });
      return h('label', {}, [input, h('span', { text: t(lang, 'ch_' + c) || c })]);
    })));
  };

  /** Channel boxes follow their purpose: usable only while it is switched on. */
  Widget.prototype.syncChannels = function (purposeId) {
    var row = this.root && this.root.querySelector('.wk-chans[data-purpose="' + purposeId + '"]');
    if (!row) return;
    var on = !!this.choices[purposeId];
    row.classList.toggle('wk-off', !on);
    row.querySelectorAll('input').forEach(function (i) { i.disabled = !on; });
  };

  // ---------------------------------------------------------------- confirmation

  function istTime(iso, lang) {
    try {
      return new Date(iso).toLocaleString(lang === 'en' ? 'en-IN' : lang + '-IN', {
        timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }) + ' IST';
    } catch (e) { return iso; }
  }
  function shortHash(x) { return x ? x.slice(0, 16) + '…' + x.slice(-4) : ''; }
  function esc(x) { return String(x == null ? '' : x).replace(/[&<>"]/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); }

  /**
   * The saved state: one record per decision. A decision writes one ledger
   * event per purpose; the card shows the first event's entry, time and hash,
   * and lists every purpose with its own entry number when there are several.
   * Returns false when there is nothing to show (no events, preview, or the
   * host turned it off), so the caller falls back to the one-line message.
   */
  Widget.prototype.renderSaved = function (receipt) {
    var self = this;
    var lang = this.lang;
    var events = (receipt && receipt.events || []).filter(function (e) { return e && e.eventHash; });
    if (!this.confirmation || this.preview || !events.length) return false;
    var decided = events.filter(function (e) { return e.action !== 'informed'; });
    var label = decided.length && decided.every(function (e) { return e.action === 'granted'; }) ? 'actGiven'
      : decided.length && decided.every(function (e) { return e.action === 'withdrawn'; }) ? 'actWithdrawn' : 'actUpdated';
    var first = events[0];
    var names = {};
    (this.config.purposes || []).forEach(function (p) { names[p.purposeId] = p.name; });
    var verifyHref = this.verifyUrl + (this.verifyUrl.indexOf('?') >= 0 ? '&' : '?') + 'h=' + first.eventHash;

    var copy = h('button', {
      class: 'wk-copy', type: 'button', text: t(lang, 'doneCopy'),
      onclick: function () {
        if (navigator.clipboard) navigator.clipboard.writeText(first.eventHash).then(function () { copy.textContent = t(lang, 'doneCopied'); }, function () {});
      },
    });
    var facts = [
      h('dt', { text: t(lang, 'doneEntry') }), h('dd', { text: first.seq ? String(first.seq) : '' }),
      h('dt', { text: t(lang, 'doneWhen') }), h('dd', { text: istTime(first.occurredAt || new Date().toISOString(), lang) }),
      h('dt', { text: t(lang, 'doneHash') }), h('dd', {}, [h('span', { class: 'wk-mono', title: first.eventHash, text: shortHash(first.eventHash) }), copy]),
    ];
    if (receipt.contentHash) facts.push(h('dt', { text: t(lang, 'doneNoticeHash') }), h('dd', { class: 'wk-mono', title: receipt.contentHash, text: shortHash(receipt.contentHash) }));
    var list = events.length > 1 ? h('div', {}, [
      h('div', { class: 'wk-done-note', text: t(lang, 'doneMore').replace('{n}', String(events.length)) }),
      h('ul', {}, events.map(function (e) {
        var what = e.action === 'granted' ? 'doneAllowed' : e.action === 'informed' ? 'doneStated' : 'doneNotAllowed';
        return h('li', { text: (names[e.purposeId] || '') + ' · ' + t(lang, what) + ' · ' + t(lang, 'doneEntry') + ' ' + e.seq });
      })),
    ]) : null;

    var receipts = null;
    function fetchReceipts() {
      if (receipts) return receipts;
      receipts = fetch(self.api + '/v1/consents/receipts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Consent-Key': self.key },
        body: JSON.stringify({ events: events.map(function (e) { return { eventId: e.eventId, eventHash: e.eventHash }; }) }),
      }).then(function (r) {
        return r.json().then(function (b) { if (!r.ok) throw new Error(b.message || t(lang, 'doneReceiptFailed')); return b.receipts; });
      });
      receipts.catch(function () { receipts = null; });
      return receipts;
    }
    function fail(err) { self.showAlert('err', (err && err.message) || t(lang, 'doneReceiptFailed')); }

    var json = h('button', {
      class: 'wk-btn', type: 'button', text: t(lang, 'doneJson'),
      onclick: function () {
        fetchReceipts().then(function (list2) {
          var data = list2.length === 1 ? list2[0] : list2;
          var a = document.createElement('a');
          a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2) + '\n'], { type: 'application/json' }));
          a.download = 'receipt-' + first.eventHash.slice(0, 12) + '.json';
          document.body.appendChild(a); a.click(); a.remove();
        }).catch(fail);
      },
    });
    var print = h('button', {
      class: 'wk-btn', type: 'button', text: t(lang, 'donePrint'),
      onclick: function () {
        // Opened inside the click so a popup blocker lets it through; filled once the receipt arrives.
        var w = window.open('', '_blank');
        fetchReceipts().then(function (list2) {
          if (!w) return;
          var rows = list2.map(function (r) {
            return '<table>' + [
              [t(lang, 'doneRecord'), r.issuer && r.issuer.name],
              [t(lang, 'doneWhen'), istTime(r.occurred_at, lang)],
              ['Action', r.action], ['Purpose', r.purpose], [t(lang, 'doneEntry'), r.seq],
              [t(lang, 'doneHash'), '<code>' + esc(r.event_hash) + '</code>'],
              ['ES256', '<code>' + esc(r.sig.kid) + '</code>'], ['ML-DSA-65', '<code>' + esc(r.pq_sig.kid) + '</code>'],
            ].map(function (x) { return '<tr><th>' + esc(x[0]) + '</th><td>' + (String(x[1]).indexOf('<code>') === 0 ? x[1] : esc(x[1])) + '</td></tr>'; }).join('') + '</table>';
          }).join('');
          w.document.write('<!doctype html><meta charset="utf-8"><title>' + esc(t(lang, 'doneRecord')) + '</title>'
            + '<style>body{font:14px system-ui,sans-serif;margin:32px;color:#0F172A}table{border-collapse:collapse;width:100%;margin:0 0 24px}'
            + 'th,td{text-align:left;vertical-align:top;padding:6px 8px;border-bottom:1px solid #E2E8F0}th{width:30%;color:#64748B;font-weight:500}code{word-break:break-all}</style>'
            + '<h1 style="font-size:20px">' + esc(t(lang, 'doneTitle')) + '</h1><p>' + esc(t(lang, 'doneLine')) + '</p>' + rows
            + '<p>' + esc(t(lang, 'doneVerify')) + ': <a href="' + esc(verifyHref) + '">' + esc(verifyHref) + '</a></p>');
          w.document.close();
          w.focus();
          w.print();
        }).catch(function (err) { if (w) w.close(); fail(err); });
      },
    });

    var panel = h('div', { class: 'wk-done', role: 'status' }, [
      h('div', { class: 'wk-done-head' }, [svg('check'), h('h2', { id: 'wk-title', text: t(lang, 'doneTitle'), tabindex: '-1' })]),
      h('p', { class: 'wk-done-line', text: t(lang, 'doneLine') }),
      h('div', { class: 'wk-rec' }, [
        h('h3', { text: t(lang, 'doneRecord') + ': ' + t(lang, label) }),
        h('dl', {}, facts),
        list,
      ]),
      h('div', { class: 'wk-done-acts' }, [
        h('a', { class: 'wk-btn', href: verifyHref, target: '_blank', rel: 'noopener', text: t(lang, 'doneVerify') }),
        print, json,
      ]),
      h('p', { class: 'wk-done-note', text: t(lang, 'doneNoticeNote') }),
      this.alertSlot = h('div'),
      h('button', {
        class: 'wk-btn wk-btn-primary', type: 'button', text: t(lang, 'doneContinue'),
        onclick: function () { self.emit('continue', { events: events }); self.render(); },
      }),
    ]);
    this.container.innerHTML = '';
    this.container.appendChild(panel);
    var head = panel.querySelector('h2');
    if (head && head.focus) head.focus();
    return true;
  };

  Widget.prototype.renderId = function () {
    if (!this._renderId) {
      this._renderId = 'rnd_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    }
    return this._renderId;
  };

  Widget.prototype.showAlert = function (kind, message) {
    this.alertSlot.innerHTML = '';
    this.alertSlot.appendChild(h('div', {
      class: 'wk-alert ' + (kind === 'ok' ? 'wk-alert-ok' : 'wk-alert-err'),
      role: kind === 'ok' ? 'status' : 'alert',
      text: message,
    }));
  };

  Widget.prototype.renderError = function (err) {
    this.container.innerHTML = '';
    this.container.appendChild(h('div', {
      class: 'wk-alert wk-alert-err',
      role: 'alert',
      text: 'Consent options could not be loaded. ' + (err && err.message ? err.message : ''),
    }));
  };

  /** The full S.5 notice, rendered from the published variant the API served. */
  Widget.prototype.openNotice = function () {
    var self = this;
    var lang = this.lang;
    var n = this.config.notice.content;

    var card = h('div', { class: 'wk-modal-card', role: 'dialog', 'aria-modal': 'true', 'aria-label': n.title }, [
      h('h2', { text: n.title }),
      h('p', { text: n.intro }),
      n.dataItemsIntro ? h('p', { text: n.dataItemsIntro }) : null,
      h('table', {}, [
        h('thead', {}, [h('tr', {}, [
          h('th', { text: 'Purpose' }), h('th', { text: 'Data' }), h('th', { text: 'Kept for' }), h('th', { text: t(lang, 'sharedWith') }),
        ])]),
        h('tbody', {}, (n.purposes || []).map(function (p) {
          return h('tr', {}, [
            h('td', {}, [h('strong', { text: p.name }), h('div', { text: p.description })]),
            h('td', { text: p.dataCategories.join(', ') }),
            h('td', { text: p.retentionDays ? p.retentionDays + ' ' + t(lang, 'days') : t(lang, 'untilWithdrawn') }),
            h('td', { text: p.processors && p.processors.length ? p.processors.join(', ') : t(lang, 'notShared') }),
          ]);
        })),
      ]),
      h('h3', { text: n.rights.heading || t(lang, 'yourRights') }),
      h('p', { text: n.rights.body }),
      h('ul', {}, (n.rights.items || []).map(function (i) { return h('li', { text: i }); })),
      h('h3', { text: n.withdrawal.heading }),
      h('p', { text: n.withdrawal.body }),
      h('p', {}, [h('a', { class: 'wk-link', href: n.withdrawal.url, target: '_blank', rel: 'noopener', text: n.withdrawal.url })]),
      h('h3', { text: n.grievance.heading || t(lang, 'grievance') }),
      h('p', { text: n.grievance.officerName + ' · ' + n.grievance.email + (n.grievance.phone ? ' · ' + n.grievance.phone : '') }),
      h('h3', { text: n.boardComplaint.heading || t(lang, 'board') }),
      h('p', { text: n.boardComplaint.body }),
      h('p', { class: 'wk-foot', text: 'Notice version ' + this.config.notice.version + ' · ' + this.config.notice.contentHash.slice(0, 12) }),
      h('div', { class: 'wk-actions' }, [
        h('button', { class: 'wk-btn', type: 'button', text: t(lang, 'close'), onclick: function () { self.closeNotice(); } }),
      ]),
    ]);

    this.modal = h('div', {
      class: 'wk-modal',
      onclick: function (e) { if (e.target === self.modal) self.closeNotice(); },
    }, [card]);
    this.root.appendChild(this.modal);
    this.escHandler = function (e) { if (e.key === 'Escape') self.closeNotice(); };
    document.addEventListener('keydown', this.escHandler);
    card.querySelector('.wk-btn').focus();
  };

  Widget.prototype.closeNotice = function () {
    if (this.modal) this.root.removeChild(this.modal);
    this.modal = null;
    if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
  };

  Widget.prototype.emit = function (name, detail) {
    var event = new CustomEvent('wekalp:consent:' + name, { detail: detail, bubbles: true });
    (this.host || document).dispatchEvent(event);
  };

  var api = {
    instances: [],
    mount: function (options) {
      var w = new Widget(options);
      api.instances.push(w);
      var mounted = w.mount();
      return mounted.then(function () { return w; });
    },
    /** Submits the most recently mounted widget — used after the host creates the account. */
    submit: function (principal) {
      var w = api.instances[api.instances.length - 1];
      if (!w) return Promise.reject(new Error('no consent widget mounted'));
      return w.submit(principal || {});
    },
  };

  window.WekalpConsent = api;

  // Auto-mount from the script tag's data attributes.
  var script = document.currentScript;
  if (script && script.dataset.key) {
    var d = script.dataset;
    var start = function () {
      api.mount({
        api: d.api || new URL(script.src, location.href).origin,
        key: d.key,
        lang: d.lang || document.documentElement.lang || 'en',
        target: d.target || '#wekalp-consent',
        identifierField: d.identifierField || null,
        identifierType: d.identifierType || 'email',
        layout: d.layout || 'card',
        hideSubmit: d.hideSubmit === 'true' || d.hideSubmit === '',
        confirmation: d.confirmation !== 'false',
        showSensitivity: d.showSensitivity === 'true' || d.showSensitivity === '',
        verifyUrl: d.verifyUrl || null,
      }).catch(function () { /* renderError already showed the failure */ });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
})();
